'use strict';

/* ============================================================
   Soccer Revolution — Quiniela Admin Panel
   Panel de administración para la Quiniela Mundial 2026
   Backend: Firebase Authentication + Cloud Firestore
   ============================================================ */

// ── SEGURIDAD: escape de HTML ───────────────────────────────
// Nombre, selección favorita y email vienen del registro público (sin
// restricción de caracteres) y se insertan vía innerHTML en esta pantalla.
// Sin escapar, un registro con "<img src=x onerror=...>" en el nombre
// ejecutaría JavaScript en el navegador del admin con sesión activa.
function escapeHTML(str) {
  return String(str === undefined || str === null ? '' : str).replace(/[&<>"']/g, c => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
  ));
}

const ADMIN_EMAIL = 'soccerrevolution.gt21@gmail.com';

// Valores de puntos por defecto — se usan mientras quiniela_config/points no
// exista todavía, o para cualquier fase que falte en ese documento (config
// parcial). Deben coincidir con QuinielaApp (sr-quiniela.js) para que el
// admin y el ranking público siempre muestren los mismos puntos.
const DEFAULT_FASE_PUNTOS = {
  dieciseisavos: { resultado: 2,  exacto: 5,  anotador: 2  },
  octavos:       { resultado: 4,  exacto: 8,  anotador: 3  },
  cuartos:       { resultado: 6,  exacto: 12, anotador: 4  },
  semifinal:     { resultado: 10, exacto: 20, anotador: 6  },
  tercer_lugar:  { resultado: 10, exacto: 20, anotador: 6  },
  final:         { resultado: 20, exacto: 50, anotador: 10 }
};
const DEFAULT_RACHA = { streak3: 5, streak5: 15, streak8: 30 };

const SRAdmin = {

  db: null,
  auth: null,

  fases: [
    { id: 'dieciseisavos', nombre: '16VOS DE FINAL', icono: '⚽', partidos: 16 },
    { id: 'octavos',       nombre: '8VOS DE FINAL',  icono: '⚡', partidos: 8  },
    { id: 'cuartos',       nombre: 'CUARTOS',         icono: '🔥', partidos: 4  },
    { id: 'semifinal',     nombre: 'SEMIFINALES',     icono: '💥', partidos: 2  },
    { id: 'tercer_lugar',  nombre: 'TERCER LUGAR',    icono: '🥉', partidos: 1  },
    { id: 'final',         nombre: 'GRAN FINAL',      icono: '🏆', partidos: 1  }
  ],

  init() {
    this.db = firebase.firestore();
    this.auth = firebase.auth();

    this.auth.onAuthStateChanged(user => {
      if (user && user.email === ADMIN_EMAIL) {
        this.showPanel();
      } else {
        if (user) this.auth.signOut(); // alguien que no es el admin quedó logueado por error
        this.showLogin();
      }
    });

    // Accesibilidad: permite activar con Enter/Espacio los divs de tab
    // (data-tab + role="button") usando solo el teclado.
    document.addEventListener('keydown', e => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      const target = e.target;
      if (target && target.getAttribute && target.getAttribute('role') === 'button' && target.tagName !== 'BUTTON') {
        e.preventDefault();
        target.click();
      }
    });
  },

  // Si la sesión del admin se invalida (ej. restableció su propia contraseña
  // en otra pestaña), la siguiente lectura a Firestore falla con
  // "permission-denied" — sin esto, se veían mensajes confusos de "verifica
  // tu conexión" en vez de pedirle que vuelva a iniciar sesión.
  isSessionError(err) {
    const code = err && err.code;
    return code === 'permission-denied' || code === 'unauthenticated' ||
      code === 'auth/user-token-expired' || code === 'auth/user-disabled';
  },

  handleSessionExpired() {
    this.auth.signOut();
    this.toast('Tu sesión expiró o cambió desde otro dispositivo. Inicia sesión de nuevo.', 'error');
    this.showLogin();
  },

  // ── AUTH ────────────────────────────────────────────────

  async login(e) {
    e.preventDefault();
    const pass = document.getElementById('sr-admin-pass')?.value || '';
    try {
      await this.auth.signInWithEmailAndPassword(ADMIN_EMAIL, pass);
      this.toast('Panel de administración abierto ⚙️', 'success');
    } catch (err) {
      this.toast('Contraseña incorrecta', 'error');
      const inp = document.getElementById('sr-admin-pass');
      if (inp) inp.value = '';
    }
  },

  logout() {
    this.auth.signOut();
  },

  showLogin() {
    const l = document.getElementById('sr-admin-login');
    const p = document.getElementById('sr-admin-panel');
    if (l) l.style.display = 'flex';
    if (p) p.style.display = 'none';
  },

  showPanel() {
    const l = document.getElementById('sr-admin-login');
    const p = document.getElementById('sr-admin-panel');
    if (l) l.style.display = 'none';
    if (p) p.style.display = 'block';
    this.renderStats();
    this.switchTab('fases');
    this._fixSemifinalVenues();
  },

  async _fixSemifinalVenues() {
    const fixes = [
      { id: 'M29', sede: 'AT&T Stadium, Arlington/Dallas' },
      { id: 'M30', sede: 'Mercedes-Benz Stadium, Atlanta' }
    ];
    try {
      const ref = this.db.collection('quiniela_config').doc('matches');
      const snap = await ref.get();
      if (!snap.exists) return;
      const matches = snap.data();
      let changed = false;
      const arr = matches['semifinal'] || [];
      fixes.forEach(({ id, sede }) => {
        const m = arr.find(p => p.id === id);
        if (m && m.sede !== sede) { m.sede = sede; changed = true; }
      });
      if (changed) await ref.set(matches);
    } catch (e) { /* no crítico */ }
  },

  // ── DATA (Firestore) ─────────────────────────────────────

  getDefaultMatches() {
    const TBD = () => ({ nombre: 'Por definir', bandera: '🏳️', escudo: '', jugadores: [] });
    const m = (id, num, fecha, sede, estado) => ({
      id, num, fecha, hora: 'Por confirmar', sede,
      local: TBD(), visitante: TBD(), resultado: null, estado
    });
    return {
      dieciseisavos: [
        m('M01', 73, '28 Jun 2026', 'SoFi Stadium, Los Ángeles', 'pendiente'),
        m('M02', 74, '29 Jun 2026', 'NRG Stadium, Houston', 'pendiente'),
        m('M03', 75, '29 Jun 2026', 'Gillette Stadium, Boston/Foxborough', 'pendiente'),
        m('M04', 76, '29 Jun 2026', 'Estadio BBVA, Monterrey', 'pendiente'),
        m('M05', 77, '30 Jun 2026', 'AT&T Stadium, Dallas/Arlington', 'pendiente'),
        m('M06', 78, '30 Jun 2026', 'MetLife Stadium, East Rutherford', 'pendiente'),
        m('M07', 79, '30 Jun 2026', 'Estadio Azteca, Ciudad de México', 'pendiente'),
        m('M08', 80, '1 Jul 2026', 'Mercedes-Benz Stadium, Atlanta', 'pendiente'),
        m('M09', 81, '1 Jul 2026', 'Lumen Field, Seattle', 'pendiente'),
        m('M10', 82, '1 Jul 2026', "Levi's Stadium, Santa Clara", 'pendiente'),
        m('M11', 83, '2 Jul 2026', 'SoFi Stadium, Los Ángeles', 'pendiente'),
        m('M12', 84, '2 Jul 2026', 'BMO Field, Toronto', 'pendiente'),
        m('M13', 85, '2 Jul 2026', 'BC Place, Vancouver', 'pendiente'),
        m('M14', 86, '3 Jul 2026', 'AT&T Stadium, Dallas/Arlington', 'pendiente'),
        m('M15', 87, '3 Jul 2026', 'Hard Rock Stadium, Miami', 'pendiente'),
        m('M16', 88, '3 Jul 2026', 'Arrowhead Stadium, Kansas City', 'pendiente')
      ],
      octavos: [
        m('M17', 89, '4 Jul 2026', 'NRG Stadium, Houston', 'bloqueado'),
        m('M18', 90, '4 Jul 2026', 'Lincoln Financial Field, Philadelphia', 'bloqueado'),
        m('M19', 91, '5 Jul 2026', 'MetLife Stadium, East Rutherford', 'bloqueado'),
        m('M20', 92, '5 Jul 2026', 'Estadio Azteca, Ciudad de México', 'bloqueado'),
        m('M21', 93, '6 Jul 2026', 'AT&T Stadium, Arlington/Dallas', 'bloqueado'),
        m('M22', 94, '6 Jul 2026', 'Lumen Field, Seattle', 'bloqueado'),
        m('M23', 95, '7 Jul 2026', 'Mercedes-Benz Stadium, Atlanta', 'bloqueado'),
        m('M24', 96, '7 Jul 2026', 'BC Place, Vancouver', 'bloqueado')
      ],
      cuartos: [
        m('M25', 97,  '9 Jul 2026',  'Gillette Stadium, Boston/Foxborough', 'bloqueado'),
        m('M26', 98,  '10 Jul 2026', 'SoFi Stadium, Los Ángeles', 'bloqueado'),
        m('M27', 99,  '11 Jul 2026', 'Hard Rock Stadium, Miami', 'bloqueado'),
        m('M28', 100, '11 Jul 2026', 'Arrowhead Stadium, Kansas City', 'bloqueado')
      ],
      semifinal: [
        m('M29', 101, '14 Jul 2026', 'AT&T Stadium, Arlington/Dallas', 'bloqueado'),
        m('M30', 102, '15 Jul 2026', 'Mercedes-Benz Stadium, Atlanta', 'bloqueado')
      ],
      tercer_lugar: [
        m('M31', 103, '18 Jul 2026', 'Hard Rock Stadium, Miami', 'bloqueado')
      ],
      final: [
        m('M32', 104, '19 Jul 2026', 'MetLife Stadium, East Rutherford, Nueva Jersey', 'bloqueado')
      ]
    };
  },

  // Lee/siembra partidos+resultados+config global. Usado por Fases, Resultados, Equipos, Premios.
  async getConfig() {
    try {
      const matchesRef = this.db.collection('quiniela_config').doc('matches');
      const matchesSnap = await matchesRef.get();
      let matches = matchesSnap.exists ? matchesSnap.data() : null;

      if (!matches) {
        matches = this.getDefaultMatches();
        await matchesRef.set(matches); // siembra inicial la primera vez que el admin entra
      } else {
        const defaults = this.getDefaultMatches();
        let changed = false;
        Object.keys(defaults).forEach(fase => {
          if (!matches[fase]) { matches[fase] = defaults[fase]; changed = true; }
          else {
            const ids = new Set(matches[fase].map(m => m.id));
            defaults[fase].forEach(m => { if (!ids.has(m.id)) { matches[fase].push(JSON.parse(JSON.stringify(m))); changed = true; } });
            matches[fase].sort((a, b) => a.num - b.num);
          }
        });
        if (changed) await matchesRef.set(matches);
      }

      const [resultsSnap, adminSnap, pointsSnap] = await Promise.all([
        this.db.collection('quiniela_config').doc('results').get(),
        this.db.collection('quiniela_config').doc('admin').get(),
        this.db.collection('quiniela_config').doc('points').get()
      ]);

      return {
        matches,
        results: resultsSnap.exists ? resultsSnap.data() : {},
        admin: adminSnap.exists ? adminSnap.data() : { fasesActivas: ['dieciseisavos'], premios: null },
        points: pointsSnap.exists ? pointsSnap.data() : null
      };
    } catch (e) {
      if (this.isSessionError(e)) { this.handleSessionExpired(); return { matches: this.getDefaultMatches(), results: {}, admin: { fasesActivas: ['dieciseisavos'], premios: null }, points: null }; }
      this.toast('Error al cargar configuración. Verifica tu conexión.', 'error');
      return { matches: this.getDefaultMatches(), results: {}, admin: { fasesActivas: ['dieciseisavos'], premios: null }, points: null };
    }
  },

  // Combina quiniela_config/points con los valores por defecto — config
  // parcial (solo algunas fases) es seguro: cada fase faltante cae en su
  // valor por defecto individualmente, nunca en un objeto vacío.
  _resolvePoints(points) {
    const resolved = {};
    Object.keys(DEFAULT_FASE_PUNTOS).forEach(faseId => {
      resolved[faseId] = Object.assign({}, DEFAULT_FASE_PUNTOS[faseId], (points && points[faseId]) || {});
    });
    return resolved;
  },

  _resolveRacha(points) {
    return Object.assign({}, DEFAULT_RACHA, (points && points.racha) || {});
  },

  async getUsersWithPredictions() {
    try {
      const [usersSnap, predsSnap] = await Promise.all([
        this.db.collection('users').get(),
        this.db.collection('predictions').get()
      ]);
      const users = [];
      usersSnap.forEach(doc => users.push({ id: doc.id, ...doc.data() }));
      const predictions = {};
      predsSnap.forEach(doc => { predictions[doc.id] = doc.data(); });
      return { users, predictions };
    } catch (e) {
      if (this.isSessionError(e)) { this.handleSessionExpired(); return { users: [], predictions: {} }; }
      this.toast('Error al cargar usuarios. Verifica tu conexión.', 'error');
      return { users: [], predictions: {} };
    }
  },

  // Modifica un partido específico dentro de quiniela_config/matches de forma
  // atómica (lee el documento completo, aplica el cambio puntual, escribe de
  // vuelta) — evita que dos guardados simultáneos se pisen entre sí.
  async mutateMatch(faseId, matchId, mutatorFn) {
    const ref = this.db.collection('quiniela_config').doc('matches');
    const snap = await ref.get();
    const matches = snap.exists ? snap.data() : this.getDefaultMatches();
    const arr = matches[faseId] || [];
    const idx = arr.findIndex(m => m.id === matchId);
    if (idx === -1) return;
    mutatorFn(arr[idx]);
    await ref.set(matches);
  },

  // ── CORRECCIÓN DE SEDES (uso único) ──────────────────────
  // El calendario oficial de sedes 2026 que se usó originalmente para
  // sembrar quiniela_config/matches tenía varios estadios mal asignados
  // (ej. M03 decía "Estadio BBVA, Monterrey" cuando en realidad le
  // correspondía a M04, y viceversa). Como el documento YA EXISTE en
  // Firestore (con equipos, resultados y predicciones reales cargados), el
  // sembrado normal de getDefaultMatches() ya no se vuelve a ejecutar — por
  // eso hace falta esta corrección puntual. SOLO toca el campo `sede` de
  // cada partido por su `id`; nunca toca equipos, resultado, estado, lockAt
  // ni jugadores. Es seguro ejecutarla más de una vez (idempotente).
  correctVenues() {
    return {
      M01: 'SoFi Stadium, Los Ángeles',
      M02: 'NRG Stadium, Houston',
      M03: 'Gillette Stadium, Boston/Foxborough',
      M04: 'Estadio BBVA, Monterrey',
      M05: 'AT&T Stadium, Dallas/Arlington',
      M06: 'MetLife Stadium, East Rutherford',
      M07: 'Estadio Azteca, Ciudad de México',
      M08: 'Mercedes-Benz Stadium, Atlanta',
      M09: 'Lumen Field, Seattle',
      M10: "Levi's Stadium, Santa Clara",
      M11: 'SoFi Stadium, Los Ángeles',
      M12: 'BMO Field, Toronto',
      M13: 'BC Place, Vancouver',
      M14: 'AT&T Stadium, Dallas/Arlington',
      M15: 'Hard Rock Stadium, Miami',
      M16: 'Arrowhead Stadium, Kansas City',
      M17: 'NRG Stadium, Houston',
      M18: 'Lincoln Financial Field, Philadelphia',
      M19: 'MetLife Stadium, East Rutherford',
      M20: 'Estadio Azteca, Ciudad de México',
      M21: 'AT&T Stadium, Arlington/Dallas',
      M22: 'Lumen Field, Seattle',
      M23: 'Mercedes-Benz Stadium, Atlanta',
      M24: 'BC Place, Vancouver',
      M25: 'Gillette Stadium, Boston/Foxborough',
      M26: 'SoFi Stadium, Los Ángeles',
      M27: 'Hard Rock Stadium, Miami',
      M28: 'Arrowhead Stadium, Kansas City',
      M29: 'AT&T Stadium, Arlington/Dallas',
      M30: 'Mercedes-Benz Stadium, Atlanta',
      M31: 'Hard Rock Stadium, Miami',
      M32: 'MetLife Stadium, East Rutherford, Nueva Jersey'
    };
  },

  async fixVenues() {
    if (!confirm('¿Corregir los nombres de las sedes de los 32 partidos? Esto NO toca equipos, resultados, predicciones ni horas de cierre — solo el campo de estadio/sede.')) return;
    const correct = this.correctVenues();
    const ref = this.db.collection('quiniela_config').doc('matches');
    let updated = 0;
    try {
      const snap = await ref.get();
      const matches = snap.exists ? snap.data() : this.getDefaultMatches();
      Object.keys(matches).forEach(faseId => {
        (matches[faseId] || []).forEach(match => {
          if (correct[match.id] && match.sede !== correct[match.id]) {
            match.sede = correct[match.id];
            updated++;
          }
        });
      });
      await ref.set(matches);
      this.toast('✅ Sedes corregidas: ' + updated + ' partido(s) actualizado(s). Equipos, resultados y predicciones quedaron intactos.', 'success');
      this.renderResultados();
    } catch (err) {
      if (this.isSessionError(err)) return this.handleSessionExpired();
      this.toast('Error al corregir sedes. Verifica tu conexión.', 'error');
    }
  },

  // ── STATS ───────────────────────────────────────────────

  async renderStats() {
    const [{ results, admin }, { users, predictions }] = await Promise.all([
      this.getConfig(), this.getUsersWithPredictions()
    ]);
    const predsTotal = Object.values(predictions).reduce((s, u) => s + Object.keys(u).length, 0);
    const set = (id, v) => { const el = document.getElementById(id); if (el) el.textContent = v; };
    set('sr-admin-stat-users',   users.length);
    set('sr-admin-stat-preds',   predsTotal);
    set('sr-admin-stat-results', Object.keys(results).length);
    set('sr-admin-stat-fases',   (admin.fasesActivas || []).length);

    // Recalcula y corrige el contador público de participantes (visible en la
    // landing antes de loguearse) a partir de la cantidad REAL de usuarios,
    // cada vez que se abre el panel. Esto autocorrige cualquier desincronía
    // (ej. quedó en -1 por incrementos/decrementos manuales anteriores).
    try {
      await this.db.collection('quiniela_config').doc('stats').set({ totalUsers: users.length });
    } catch (e) { /* no crítico si falla, se reintenta la próxima vez */ }
  },

  // ── TABS ────────────────────────────────────────────────

  switchTab(tab) {
    document.querySelectorAll('.sr-q-admin-tab').forEach(t => {
      t.classList.toggle('active', t.dataset.tab === tab);
    });
    document.querySelectorAll('.sr-admin-tab-content').forEach(c => {
      c.style.display = c.dataset.tab === tab ? 'block' : 'none';
    });
    const renders = {
      fases:          () => this.renderFases(),
      resultados:     () => this.renderResultados(),
      equipos:        () => this.renderEquipos(),
      usuarios:       () => this.renderUsuarios(),
      premios:        () => this.renderPremios(),
      puntos:         () => this.renderPuntos(),
      patrocinadores: () => this.renderPatrocinadores(),
      vista:          () => this.renderVista()
    };
    if (renders[tab]) renders[tab]();
  },

  // ── TAB: FASES ──────────────────────────────────────────

  async renderFases() {
    const container = document.getElementById('sr-admin-fases-content');
    if (!container) return;
    container.innerHTML = '<p style="color:#666">Cargando…</p>';

    const { admin } = await this.getConfig();
    const fasesActivas = admin.fasesActivas || [];

    const ptsMap = {
      dieciseisavos: 'Resultado +2, Exacto +5, Anotador +2',
      octavos:       'Resultado +4, Exacto +8, Anotador +3',
      cuartos:       'Resultado +6, Exacto +12, Anotador +4',
      semifinal:     'Resultado +10, Exacto +20, Anotador +6',
      tercer_lugar:  'Resultado +10, Exacto +20, Anotador +6',
      final:         'Resultado +20, Exacto +50, Anotador +10'
    };

    container.innerHTML = this.fases.map(fase => {
      const isActive = fasesActivas.includes(fase.id);
      return '<div class="sr-q-admin-card" style="display:flex;align-items:center;' +
        'justify-content:space-between;gap:16px;flex-wrap:wrap">' +
        '<div style="display:flex;align-items:center;gap:16px">' +
          '<span style="font-size:36px">' + fase.icono + '</span>' +
          '<div>' +
            '<div style="font-family:\'Bebas Neue\',sans-serif;font-size:20px;' +
              'color:#D4A017;letter-spacing:2px">' + fase.nombre + '</div>' +
            '<div style="font-size:12px;color:' + (isActive ? '#22c55e' : '#555') + ';margin-top:4px">' +
              (isActive ? '✅ Abierta para predicciones' : '🔒 Cerrada') +
            '</div>' +
            '<div style="font-size:11px;color:#444;margin-top:2px">' + ptsMap[fase.id] + '</div>' +
          '</div>' +
        '</div>' +
        '<label style="display:flex;align-items:center;gap:10px;cursor:pointer">' +
          '<input type="checkbox" ' + (isActive ? 'checked' : '') + ' ' +
            'onchange="SRAdmin.toggleFase(\'' + fase.id + '\',this.checked)" ' +
            'style="width:22px;height:22px;accent-color:#D4A017;cursor:pointer">' +
          '<span style="font-family:\'Bebas Neue\',sans-serif;font-size:15px;' +
            'color:' + (isActive ? '#22c55e' : '#666') + ';letter-spacing:1px">' +
            (isActive ? 'DESACTIVAR' : 'ACTIVAR') +
          '</span>' +
        '</label>' +
      '</div>';
    }).join('');
  },

  async toggleFase(faseId, enabled) {
    const ref = this.db.collection('quiniela_config').doc('admin');
    try {
      const snap = await ref.get();
      const admin = snap.exists ? snap.data() : { fasesActivas: ['dieciseisavos'], premios: null };
      if (!admin.fasesActivas) admin.fasesActivas = [];
      if (enabled) {
        if (!admin.fasesActivas.includes(faseId)) admin.fasesActivas.push(faseId);
      } else {
        admin.fasesActivas = admin.fasesActivas.filter(f => f !== faseId);
      }
      await ref.set(admin);

      // Al activar una fase, desbloquear automáticamente todos sus partidos
      // que siguen en 'bloqueado' — sin esto, la fase aparece activa pero
      // los usuarios no pueden predecir ningún partido porque todos muestran
      // el candado. Usamos update() con la clave de la fase para no tocar
      // los otros datos del documento de partidos.
      if (enabled) {
        try {
          const matchesRef = this.db.collection('quiniela_config').doc('matches');
          const matchesSnap = await matchesRef.get();
          if (matchesSnap.exists) {
            const arr = matchesSnap.data()[faseId] || [];
            if (arr.some(m => m.estado === 'bloqueado')) {
              const unlocked = arr.map(m => m.estado === 'bloqueado' ? { ...m, estado: 'pendiente' } : m);
              await matchesRef.update({ [faseId]: unlocked });
            }
          }
        } catch (_) { /* no crítico — el admin puede usar el botón manual */ }
      }

      this.toast('Fase ' + (enabled ? 'activada ✅ — partidos abiertos para predicciones' : 'desactivada 🔒'), 'success');
      this.renderFases();
      this.renderStats();
    } catch (e) {
      if (this.isSessionError(e)) return this.handleSessionExpired();
      this.toast('Error al guardar. Verifica tu conexión.', 'error');
    }
  },

  // Abre un partido bloqueado para predicciones (estado: bloqueado → pendiente).
  // Útil cuando el admin quiere habilitar predicciones individualmente sin
  // tener que desactivar y reactivar la fase completa.
  async openForPredictions(matchId, faseId) {
    try {
      await this.mutateMatch(faseId, matchId, match => {
        if (match.estado === 'bloqueado') match.estado = 'pendiente';
      });
      this.toast('🔓 Partido abierto para predicciones', 'success');
      this.renderResultados();
    } catch (err) {
      if (this.isSessionError(err)) return this.handleSessionExpired();
      this.toast('Error al abrir el partido. Verifica tu conexión.', 'error');
    }
  },

  _resolveStadium(partido) {
    const names = ((partido.local?.nombre || '') + ' ' + (partido.visitante?.nombre || '')).toLowerCase();
    const map = [
      { keys: ['canad','marruecos','morocco'],           sede: 'NRG Stadium, Houston' },
      { keys: ['paragua','franc'],                        sede: 'Lincoln Financial Field, Philadelphia' },
      { keys: ['brasil','brazil','noruega','norway'],     sede: 'MetLife Stadium, East Rutherford' },
      { keys: ['méx','mexico','inglat','england'],        sede: 'Estadio Azteca, Ciudad de México' },
      { keys: ['portug','croac','espa','spain'],          sede: 'AT&T Stadium, Arlington/Dallas' },
      { keys: ['estados','eeuu','usa','bélg','belg'],     sede: 'Lumen Field, Seattle' },
      { keys: ['argentin','cabo','austral','egipt'],      sede: 'Mercedes-Benz Stadium, Atlanta' },
      { keys: ['suiza','argel','colombia','ghana'],       sede: 'BC Place, Vancouver' },
    ];
    for (const s of map) { if (s.keys.some(k => names.includes(k))) return s.sede; }
    return partido.sede || '';
  },

  // ── MIGRACIÓN: CORREGIR ESTADIOS OCTAVOS ────────────────

  async fixOctavosStadiums() {
    const ref = this.db.collection('quiniela_config').doc('matches');
    const snap = await ref.get();
    const data = snap.data() || {};
    const octavos = (data.octavos || []).map(m => ({ ...m }));

    if (octavos.length === 0) return this.toast('No hay partidos de 8vos cargados.', 'error');

    const sedeMap = [
      { keys: ['canad', 'marruecos', 'morocco'],            sede: 'NRG Stadium, Houston' },
      { keys: ['paragua', 'franc'],                          sede: 'Lincoln Financial Field, Philadelphia' },
      { keys: ['brasil', 'brazil', 'noruega', 'norway'],    sede: 'MetLife Stadium, East Rutherford' },
      { keys: ['méx', 'mexico', 'inglat', 'england'],       sede: 'Estadio Azteca, Ciudad de México' },
      { keys: ['portug', 'croac', 'espa', 'spain'],         sede: 'AT&T Stadium, Arlington/Dallas' },
      { keys: ['estados', 'eeuu', 'usa', 'bélg', 'belg'],  sede: 'Lumen Field, Seattle' },
      { keys: ['argentin', 'cabo', 'austral', 'egipt'],     sede: 'Mercedes-Benz Stadium, Atlanta' },
      { keys: ['suiza', 'argel', 'colombia', 'ghana'],      sede: 'BC Place, Vancouver' },
    ];

    // Stadiums para partidos Por Definir (en orden cronológico)
    const porDefinirSedes = ['Mercedes-Benz Stadium, Atlanta', 'BC Place, Vancouver'];

    const matched = new Set();
    octavos.forEach(match => {
      const names = ((match.local?.nombre || '') + ' ' + (match.visitante?.nombre || '')).toLowerCase();
      for (const slot of sedeMap) {
        if (slot.keys.some(k => names.includes(k))) {
          match.sede = slot.sede;
          matched.add(match.id);
          break;
        }
      }
    });

    // Por Definir: asignar sedes restantes en orden por lockAt
    let pdi = 0;
    octavos
      .filter(m => !matched.has(m.id))
      .sort((a, b) => {
        if (a.lockAt && b.lockAt) return new Date(a.lockAt) - new Date(b.lockAt);
        return a.num - b.num;
      })
      .forEach(m => {
        const idx = octavos.findIndex(o => o.id === m.id);
        if (idx !== -1 && pdi < porDefinirSedes.length) {
          octavos[idx].sede = porDefinirSedes[pdi++];
        }
      });

    const preview = octavos.map((m, i) =>
      (i + 1) + '. ' + (m.local?.nombre || '?') + ' vs ' + (m.visitante?.nombre || '?') +
      '\n   📍 ' + m.sede
    ).join('\n');

    if (!confirm('⚠️ Esto reordena estadios por coincidencia de nombre de equipo.\n\n' + preview +
      '\n\nSolo se modifica el campo "estadio" (nunca equipos, resultados ni vaticinios), pero si ya hay ' +
      'predicciones guardadas para estos partidos, sus datos de sede visibles podrían cambiar. ' +
      '¿Aplicar de todas formas?')) return;

    await ref.set({ octavos }, { merge: true });
    this.toast('✅ Estadios de 8vos corregidos.', 'success');
    setTimeout(() => this.renderResultados(), 800);
  },

  // ── MIGRACIÓN: ORDENAR OCTAVOS ──────────────────────────

  async fixOctavosOrder() {
    const ref = this.db.collection('quiniela_config').doc('matches');
    const snap = await ref.get();
    const octavos = (snap.data() || {}).octavos || [];

    if (octavos.length === 0) return this.toast('No hay partidos de 8vos cargados.', 'error');

    // Horarios oficiales (Guatemala UTC-6)
    const schedule = [
      { num:1, keys:['canad','marruecos','morocco'],           fecha:'4 Jul 2026', hora:'11:00 AM', lockAt:'2026-07-04T17:00:00.000Z' },
      { num:2, keys:['paragua','franc'],                        fecha:'4 Jul 2026', hora:'3:00 PM',  lockAt:'2026-07-04T21:00:00.000Z' },
      { num:3, keys:['brasil','brazil','noruega','norway'],     fecha:'5 Jul 2026', hora:'2:00 PM',  lockAt:'2026-07-05T20:00:00.000Z' },
      { num:4, keys:['méx','mexico','inglat','england'],        fecha:'5 Jul 2026', hora:'6:00 PM',  lockAt:'2026-07-06T00:00:00.000Z' },
      { num:5, keys:['portug','croac','espa','spain'],          fecha:'6 Jul 2026', hora:'1:00 PM',  lockAt:'2026-07-06T19:00:00.000Z' },
      { num:6, keys:['estados','eeuu','usa','bélg','belg'],     fecha:'6 Jul 2026', hora:'6:00 PM',  lockAt:'2026-07-07T00:00:00.000Z' },
      { num:7, keys:['argentin','cabo','austral','egipt'],      fecha:'7 Jul 2026', hora:'10:00 AM', lockAt:'2026-07-07T16:00:00.000Z' },
      { num:8, keys:['suiza','argel','colombia','ghana'],       fecha:'7 Jul 2026', hora:'2:00 PM',  lockAt:'2026-07-07T20:00:00.000Z' },
    ];

    const result = new Array(8).fill(null);
    const used = new Set();

    schedule.forEach(slot => {
      const match = octavos.find((m, i) => {
        if (used.has(i)) return false;
        const names = ((m.local?.nombre || '') + ' ' + (m.visitante?.nombre || '')).toLowerCase();
        return slot.keys.some(k => names.includes(k));
      });
      if (match) {
        used.add(octavos.indexOf(match));
        result[slot.num - 1] = { ...match, num: slot.num, fecha: slot.fecha, hora: slot.hora, lockAt: slot.lockAt };
      }
    });

    // Partidos no identificados (Por definir): ordenar por lockAt antes de colocar
    const unmatched = octavos
      .filter((_, i) => !used.has(i))
      .sort((a, b) => {
        if (a.lockAt && b.lockAt) return new Date(a.lockAt) - new Date(b.lockAt);
        if (a.lockAt) return -1;
        if (b.lockAt) return 1;
        return a.num - b.num;
      });
    unmatched.forEach(m => {
      const empty = result.findIndex(r => !r);
      if (empty !== -1) result[empty] = m;
    });

    const newOctavos = result.filter(Boolean);

    const preview = newOctavos.map((m, i) =>
      (i + 1) + '. ' + (m.local?.nombre || '?') + ' vs ' + (m.visitante?.nombre || '?') +
      ' — ' + m.fecha + ' ' + m.hora
    ).join('\n');

    if (!confirm('⚠️ Esto reordena los partidos de 8vos por coincidencia de nombre de equipo (número, fecha, hora y hora de cierre).\n\n' + preview +
      '\n\nJugadores, resultados y vaticinios quedan intactos (las predicciones están ligadas al ID del partido, no a su número), ' +
      'pero si algún participante ya predijo estos partidos, el orden/fecha que ve en pantalla podría cambiar. ' +
      '¿Aplicar de todas formas?')) return;

    await ref.set({ octavos: newOctavos }, { merge: true });
    this.toast('✅ Orden de 8vos corregido. Recarga para ver los cambios.', 'success');
    setTimeout(() => this.renderResultados(), 800);
  },

  // ── TAB: RESULTADOS ─────────────────────────────────────

  async renderResultados() {
    const container = document.getElementById('sr-admin-resultados-content');
    if (!container) return;
    container.innerHTML = '<p style="color:#666">Cargando…</p>';

    const { matches, results } = await this.getConfig();
    let html =
      '<div style="margin-bottom:20px;padding:14px 16px;background:#1a2a1a;border:1px solid rgba(74,222,128,0.3);' +
        'border-radius:10px;display:flex;align-items:flex-start;justify-content:space-between;flex-wrap:wrap;gap:12px">' +
        '<div>' +
          '<div style="font-weight:700;color:#4ade80;font-size:14px">🔧 Herramientas: 8vos de Final</div>' +
          '<div style="font-size:12px;color:#888;margin-top:2px">' +
            'Aplica una sola vez cada herramienta. Solo modifican orden/sede, nunca borran datos.' +
          '</div>' +
        '</div>' +
        '<div style="display:flex;gap:8px;flex-wrap:wrap">' +
          '<button onclick="SRAdmin.fixOctavosOrder()" ' +
            'style="background:#4ade80;color:#0a1a0a;border:none;padding:9px 16px;border-radius:8px;' +
              'font-weight:700;font-size:13px;cursor:pointer;white-space:nowrap">' +
            '📅 Ordenar fechas' +
          '</button>' +
          '<button onclick="SRAdmin.fixOctavosStadiums()" ' +
            'style="background:#4ade80;color:#0a1a0a;border:none;padding:9px 16px;border-radius:8px;' +
              'font-weight:700;font-size:13px;cursor:pointer;white-space:nowrap">' +
            '🏟️ Corregir estadios' +
          '</button>' +
        '</div>' +
      '</div>';

    this.fases.forEach(fase => {
      const partidos = (matches[fase.id] || []).slice().sort((a, b) => {
        if (a.lockAt && b.lockAt) {
          const da = new Date(a.lockAt), db = new Date(b.lockAt);
          if (da.toDateString() === db.toDateString()) return a.num - b.num;
          return da - db;
        }
        if (a.lockAt) return -1;
        if (b.lockAt) return 1;
        return a.num - b.num;
      });
      if (partidos.length === 0) return;

      html += '<div class="sr-q-admin-card-title">' + fase.icono + ' ' + fase.nombre + '</div>';

      partidos.forEach(p => {
        const result = results[p.id];
        const isFinished = p.estado === 'finalizado';
        const isBlocked = p.estado === 'bloqueado';
        const lockVal = p.lockAt ? this.toLocalInputValue(p.lockAt) : '';
        const isLocked = p.lockAt && new Date() > new Date(p.lockAt);

        const statusBg    = isFinished ? '#1a3a1a' : isBlocked ? '#2a1a0a' : '#2a2a0a';
        const statusColor = isFinished ? '#22c55e' : isBlocked ? '#f97316' : '#f59e0b';
        // "BLOQUEADO" (manual, vía estado) y "CERRADO POR HORA" (automático, vía lockAt) son dos
        // mecanismos independientes — este label aclara cuál aplica para que el admin no tenga que
        // adivinar por qué un partido no acepta predicciones.
        const statusLabel = isFinished ? '✅ FINALIZADO' : isBlocked ? '🔒 BLOQUEADO (manual)' : '⏳ PENDIENTE';

        html += '<div class="sr-q-admin-card" style="margin-bottom:12px">' +

          '<div style="display:flex;justify-content:space-between;align-items:flex-start;' +
            'flex-wrap:wrap;gap:12px;margin-bottom:16px">' +
            '<div>' +
              '<span style="font-family:\'Bebas Neue\',sans-serif;font-size:16px;' +
                'color:#D4A017;letter-spacing:2px">PARTIDO ' + p.num + '</span>' +
              '<div style="font-size:15px;color:#fff;margin-top:4px">' +
                escapeHTML(p.local.bandera) + ' ' + escapeHTML(p.local.nombre) + ' vs ' +
                escapeHTML(p.visitante.nombre) + ' ' + escapeHTML(p.visitante.bandera) +
              '</div>' +
              '<div style="font-size:12px;color:#555;margin-top:2px">' + p.fecha + ' · ' + p.hora + ' · ' + this._resolveStadium(p) + '</div>' +
            '</div>' +
            '<div style="display:flex;flex-direction:column;gap:6px;align-items:flex-end">' +
              '<span style="font-size:12px;padding:4px 12px;border-radius:20px;font-weight:600;' +
                'background:' + statusBg + ';color:' + statusColor + '">' +
                statusLabel +
              '</span>' +
              (isBlocked && !isFinished
                ? '<button onclick="SRAdmin.openForPredictions(\'' + p.id + '\',\'' + fase.id + '\')" ' +
                    'style="background:#1d4ed8;color:#fff;border:none;padding:5px 12px;border-radius:6px;' +
                    'font-size:12px;cursor:pointer;white-space:nowrap;font-weight:600">🔓 Abrir predicciones</button>'
                : '') +
              (isLocked && !isBlocked
                ? '<span style="font-size:11px;padding:3px 10px;border-radius:20px;background:#1a1a2a;color:#818cf8">⏱️ CERRADO POR HORA DE CIERRE</span>'
                : (!isBlocked && !isFinished && lockVal ? '<span style="font-size:11px;color:#555">Cierra: ' + lockVal.replace('T',' ') + '</span>' : '')) +
            '</div>' +
          '</div>' +

          (isFinished && result
            ? '<div style="background:#1a1a1a;border-radius:8px;padding:12px 16px;' +
                'margin-bottom:12px;display:flex;gap:24px;align-items:center;flex-wrap:wrap">' +
                '<span style="font-family:\'Bebas Neue\',sans-serif;font-size:28px;' +
                  'color:#D4A017">' + result.local + ' — ' + result.visitante + '</span>' +
                ((result.scorers && result.scorers.length) || result.scorer ? '<span style="font-size:13px;color:#888">⚽ ' + (result.scorers || [result.scorer]).map(s => escapeHTML(s)).join(' · ⚽ ') + '</span>' : '') +
              '</div>'
            : '') +

          '<form class="sr-q-result-form" onsubmit="SRAdmin.saveResult(event,\'' + p.id + '\',\'' + fase.id + '\')" ' +
            'style="display:grid;grid-template-columns:1fr 1fr 1fr 1fr auto;gap:10px;align-items:end;margin-bottom:12px">' +
            '<div>' +
              '<label style="font-size:11px;color:#666;letter-spacing:1px;display:block;margin-bottom:4px">' +
                escapeHTML(p.local.nombre.toUpperCase()) + '</label>' +
              '<input type="number" name="local" min="0" max="20" value="' + (result ? result.local : '') + '" ' +
                'placeholder="0" required style="width:100%;padding:10px;background:#111;border:1px solid #333;' +
                'border-radius:6px;color:#fff;font-family:\'Bebas Neue\',sans-serif;font-size:20px;box-sizing:border-box">' +
            '</div>' +
            '<div>' +
              '<label style="font-size:11px;color:#666;letter-spacing:1px;display:block;margin-bottom:4px">' +
                escapeHTML(p.visitante.nombre.toUpperCase()) + '</label>' +
              '<input type="number" name="visitante" min="0" max="20" value="' + (result ? result.visitante : '') + '" ' +
                'placeholder="0" required style="width:100%;padding:10px;background:#111;border:1px solid #333;' +
                'border-radius:6px;color:#fff;font-family:\'Bebas Neue\',sans-serif;font-size:20px;box-sizing:border-box">' +
            '</div>' +
            '<div>' +
              '<label style="font-size:11px;color:#666;letter-spacing:1px;display:block;margin-bottom:4px">' +
                'ANOTADOR 1</label>' +
              '<select name="scorer" style="width:100%;padding:10px;background:#111;border:1px solid #333;' +
                'border-radius:6px;color:#fff;font-size:14px;box-sizing:border-box">' +
                this.scorerOptions(p, result && (result.scorers ? result.scorers[0] : result.scorer)) +
              '</select>' +
            '</div>' +
            '<div>' +
              '<label style="font-size:11px;color:#666;letter-spacing:1px;display:block;margin-bottom:4px">' +
                'ANOTADOR 2 (opcional)</label>' +
              '<select name="scorer2" style="width:100%;padding:10px;background:#111;border:1px solid #333;' +
                'border-radius:6px;color:#fff;font-size:14px;box-sizing:border-box">' +
                this.scorerOptions(p, result && result.scorers && result.scorers[1]) +
              '</select>' +
            '</div>' +
            '<button type="submit" class="sr-q-btn sr-q-btn-primary" style="padding:10px 20px;font-size:14px;' +
              'white-space:nowrap">' + (isFinished ? '🔄 ACTUALIZAR' : '✅ GUARDAR') + '</button>' +
          '</form>' +

          '<div style="border-top:1px solid #2a2a2a;padding-top:12px">' +
            '<form onsubmit="SRAdmin.saveLockTime(event,\'' + p.id + '\',\'' + fase.id + '\')" ' +
              'style="display:flex;gap:10px;align-items:flex-end;flex-wrap:wrap">' +
              '<div style="flex:1;min-width:200px">' +
                '<label style="font-size:11px;color:#555;letter-spacing:1px;display:block;margin-bottom:4px">' +
                  '🔒 BLOQUEAR PREDICCIONES EN (fecha y hora local)</label>' +
                '<input type="datetime-local" name="lockAt" value="' + lockVal + '" ' +
                  'style="width:100%;padding:9px 10px;background:#111;border:1px solid #333;' +
                  'border-radius:6px;color:#ccc;font-size:13px;box-sizing:border-box">' +
              '</div>' +
              '<button type="submit" class="sr-q-btn sr-q-btn-dark" ' +
                'style="padding:9px 16px;font-size:13px;border-color:#555;white-space:nowrap">' +
                '💾 GUARDAR CIERRE' +
              '</button>' +
              (lockVal
                ? '<button type="button" onclick="SRAdmin.clearLockTime(\'' + p.id + '\',\'' + fase.id + '\')" ' +
                    'class="sr-q-btn sr-q-btn-dark" style="padding:9px 12px;font-size:12px;border-color:#ef4444;color:#ef4444;white-space:nowrap">' +
                    '✕ QUITAR' +
                  '</button>'
                : '') +
            '</form>' +
          '</div>' +

        '</div>';
      });
    });

    container.innerHTML = html || '<div class="sr-q-empty"><p>No hay partidos cargados.</p></div>';
  },

  // Genera las <option> del selector de Anotador Estrella a partir de la
  // plantilla real del partido (pestaña Equipos). Antes era un <input> de
  // texto libre: si el admin tecleaba el nombre distinto a como está en la
  // plantilla (mayúsculas, espacios, tilde), la comparación exacta del lado
  // del usuario (pred.scorer === result.scorer) fallaba en silencio y nadie
  // recibía el bono de Anotador aunque hubieran acertado. Un <select> con las
  // mismas cadenas exactas de la plantilla elimina ese riesgo de raíz.
  scorerOptions(partido, currentScorer) {
    const players = [].concat(partido.local.jugadores || [], partido.visitante.jugadores || []);
    let html = '<option value="">Sin anotador</option>';
    html += players.map(j =>
      '<option value="' + escapeHTML(j.nombre) + '"' + (currentScorer === j.nombre ? ' selected' : '') + '>' +
        escapeHTML(j.nombre) + ' (#' + escapeHTML(j.dorsal || '?') + ')' +
      '</option>'
    ).join('');
    // Si el resultado ya tenía un anotador que ya no está en la plantilla (ej. se
    // eliminó el jugador después de guardar), se conserva como opción para no
    // perder el dato silenciosamente al volver a guardar el formulario.
    if (currentScorer && !players.some(j => j.nombre === currentScorer)) {
      html += '<option value="' + escapeHTML(currentScorer) + '" selected>' +
        escapeHTML(currentScorer) + ' (ya no está en la plantilla)</option>';
    }
    return html;
  },

  // Convierte un ISO string UTC (guardado) al formato local que espera
  // un input datetime-local (YYYY-MM-DDTHH:mm en hora local del navegador).
  toLocalInputValue(iso) {
    const d = new Date(iso);
    const pad = n => String(n).padStart(2, '0');
    return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate()) +
      'T' + pad(d.getHours()) + ':' + pad(d.getMinutes());
  },

  async saveResult(e, matchId, faseId) {
    e.preventDefault();
    const form = e.target;
    const local     = parseInt(form.querySelector('[name="local"]').value);
    const visitante = parseInt(form.querySelector('[name="visitante"]').value);
    const scorer    = form.querySelector('[name="scorer"]').value.trim() || null;
    const scorer2   = form.querySelector('[name="scorer2"]').value.trim() || null;
    const scorers   = [scorer, scorer2].filter(Boolean);

    if (isNaN(local) || isNaN(visitante)) {
      return this.toast('Ingresa marcadores válidos', 'error');
    }
    if (local < 0 || local > 20 || visitante < 0 || visitante > 20) {
      return this.toast('El marcador debe estar entre 0 y 20', 'error');
    }

    const resultData = { local, visitante, scorer: scorer || null, scorers: scorers.length ? scorers : null, faseId, savedAt: new Date().toISOString() };

    try {
      const matchesRef = this.db.collection('quiniela_config').doc('matches');
      const resultsRef = this.db.collection('quiniela_config').doc('results');

      // Leer matches (1 round trip) para actualizar el estado del partido
      const matchesSnap = await matchesRef.get();
      const matches = matchesSnap.exists ? matchesSnap.data() : this.getDefaultMatches();
      const arr = matches[faseId] || [];
      const idx = arr.findIndex(m => m.id === matchId);
      if (idx !== -1) {
        arr[idx].estado = 'finalizado';
        arr[idx].resultado = { local, visitante };
      }

      // Escribir ambos documentos en un batch atómico (1 round trip, sin retries).
      // Para results usamos merge:true — solo agrega/sobreescribe esta clave,
      // nunca borra los resultados de otros partidos ya guardados.
      const batch = this.db.batch();
      batch.set(matchesRef, matches);
      batch.set(resultsRef, { [matchId]: resultData }, { merge: true });
      await batch.commit();

      this.toast('✅ Resultado guardado: ' + local + '–' + visitante +
        (scorers.length ? ' · ⚽ ' + scorers.join(' & ') : '') + '. Puntos actualizados.', 'success');
      this.renderResultados();
      this.renderStats();
      // La tabla de Usuarios muestra puntos derivados de resultados — sin esto
      // quedaba desactualizada hasta cambiar de pestaña y volver.
      this.renderUsuarios();
    } catch (err) {
      console.error('[SR Admin] saveResult:', err.code, err.message);
      if (this.isSessionError(err)) return this.handleSessionExpired();
      this.toast('Error al guardar [' + (err.code || err.message || 'sin código') + ']. ' +
        (err.code === 'permission-denied' ? 'Inicia sesión de nuevo.' :
         err.code === 'unavailable'       ? 'Sin conexión a internet.' :
         'Verifica tu conexión.'), 'error');
    }
  },

  async saveLockTime(e, matchId, faseId) {
    e.preventDefault();
    const form = e.target;
    const lockAt = form.querySelector('[name="lockAt"]').value;
    if (!lockAt) return this.toast('Selecciona una fecha y hora de cierre', 'error');

    try {
      await this.mutateMatch(faseId, matchId, match => {
        match.lockAt = new Date(lockAt).toISOString();
      });
      this.toast('🔒 Cierre guardado: ' + lockAt.replace('T', ' '), 'success');
      this.renderResultados();
    } catch (err) {
      if (this.isSessionError(err)) return this.handleSessionExpired();
      this.toast('Error al guardar. Verifica tu conexión.', 'error');
    }
  },

  async clearLockTime(matchId, faseId) {
    try {
      await this.mutateMatch(faseId, matchId, match => { delete match.lockAt; });
      this.toast('✅ Hora de cierre eliminada', 'info');
      this.renderResultados();
    } catch (err) {
      if (this.isSessionError(err)) return this.handleSessionExpired();
      this.toast('Error. Verifica tu conexión.', 'error');
    }
  },

  // ── TAB: EQUIPOS ────────────────────────────────────────

  async renderEquipos() {
    const container = document.getElementById('sr-admin-equipos-content');
    if (!container) return;
    container.innerHTML = '<p style="color:#666">Cargando…</p>';

    const { matches } = await this.getConfig();
    let html = '<p style="color:#888;font-size:14px;margin-bottom:20px">' +
      'Edita los nombres y banderas de los equipos en cualquier fase. Los cambios se reflejan inmediatamente para todos los participantes.</p>';

    this.fases.forEach(fase => {
      const partidos = (matches[fase.id] || []).slice().sort((a, b) => {
        if (a.lockAt && b.lockAt) {
          const da = new Date(a.lockAt), db = new Date(b.lockAt);
          if (da.toDateString() === db.toDateString()) return a.num - b.num;
          return da - db;
        }
        if (a.lockAt) return -1;
        if (b.lockAt) return 1;
        return a.num - b.num;
      });
      if (partidos.length === 0) return;

      html += '<div class="sr-q-admin-card-title">' + fase.icono + ' ' + fase.nombre + '</div>';
      partidos.forEach(p => {
        html += '<div class="sr-q-admin-card" style="margin-bottom:12px">' +
          '<div style="font-family:\'Bebas Neue\',sans-serif;color:#D4A017;' +
            'letter-spacing:2px;margin-bottom:12px">PARTIDO ' + p.num + '</div>' +
          '<form class="sr-q-teams-form" onsubmit="SRAdmin.saveTeams(event,\'' + p.id + '\',\'' + fase.id + '\')" ' +
            'style="display:grid;grid-template-columns:1fr auto 1fr auto;gap:10px;align-items:end">' +
            '<div>' +
              '<label style="font-size:11px;color:#666;display:block;margin-bottom:4px">EQUIPO LOCAL</label>' +
              '<input name="localBandera" value="' + escapeHTML(p.local.bandera) + '" placeholder="🏳️" ' +
                'style="width:60px;padding:8px;background:#111;border:1px solid #333;' +
                'border-radius:6px;color:#fff;font-size:18px;text-align:center;box-sizing:border-box">' +
              '<input name="localNombre" value="' + escapeHTML(p.local.nombre) + '" placeholder="País" ' +
                'style="width:calc(100% - 68px);margin-left:8px;padding:10px;background:#111;' +
                'border:1px solid #333;border-radius:6px;color:#fff;font-size:14px;box-sizing:border-box">' +
            '</div>' +
            '<span style="font-family:\'Bebas Neue\',sans-serif;color:#333;font-size:18px">VS</span>' +
            '<div>' +
              '<label style="font-size:11px;color:#666;display:block;margin-bottom:4px">EQUIPO VISITANTE</label>' +
              '<input name="visitBandera" value="' + escapeHTML(p.visitante.bandera) + '" placeholder="🏳️" ' +
                'style="width:60px;padding:8px;background:#111;border:1px solid #333;' +
                'border-radius:6px;color:#fff;font-size:18px;text-align:center;box-sizing:border-box">' +
              '<input name="visitNombre" value="' + escapeHTML(p.visitante.nombre) + '" placeholder="País" ' +
                'style="width:calc(100% - 68px);margin-left:8px;padding:10px;background:#111;' +
                'border:1px solid #333;border-radius:6px;color:#fff;font-size:14px;box-sizing:border-box">' +
            '</div>' +
            '<button type="submit" class="sr-q-btn sr-q-btn-dark" ' +
              'style="padding:10px 16px;font-size:13px;border-color:#555">💾 GUARDAR</button>' +
          '</form>' +

          '<div class="sr-q-roster-grid" style="border-top:1px solid #2a2a2a;margin-top:16px;padding-top:16px;' +
            'display:grid;grid-template-columns:1fr 1fr;gap:16px">' +
            this.renderPlayerRoster(p, fase.id, 'local', p.local.nombre) +
            this.renderPlayerRoster(p, fase.id, 'visitante', p.visitante.nombre) +
          '</div>' +
        '</div>';
      });
    });

    container.innerHTML = html;
  },

  // ── PLANTILLAS / JUGADORES (Anotador Estrella) ──────────

  renderPlayerRoster(partido, faseId, side, teamLabel) {
    const team = side === 'local' ? partido.local : partido.visitante;
    const jugadores = team.jugadores || [];
    const inputId = side + '-' + partido.id;

    const list = jugadores.length > 0
      ? jugadores.map((j, i) =>
          '<div style="display:flex;align-items:center;justify-content:space-between;' +
            'background:#111;padding:6px 10px;border-radius:6px;font-size:13px;margin-bottom:6px">' +
            '<span><strong style="color:#D4A017">#' + escapeHTML(j.dorsal || '?') + '</strong> ' + escapeHTML(j.nombre) + '</span>' +
            '<button type="button" onclick="SRAdmin.removePlayer(\'' + partido.id + '\',\'' + faseId + '\',\'' + side + '\',' + i + ')" ' +
              'style="background:#2a0808;border:1px solid #ef4444;color:#ef4444;' +
              'padding:2px 8px;border-radius:4px;cursor:pointer;font-size:12px">✕</button>' +
          '</div>'
        ).join('')
      : '<p style="color:#444;font-size:12px;margin:0 0 8px">Sin jugadores cargados.</p>';

    return '<div>' +
      '<label style="font-size:11px;color:#666;letter-spacing:1px;display:block;margin-bottom:8px">' +
        '⚽ JUGADORES — ' + escapeHTML(teamLabel) +
      '</label>' +
      '<div>' + list + '</div>' +
      '<div style="display:flex;gap:6px;margin-top:4px">' +
        '<input id="np-dorsal-' + inputId + '" type="number" min="1" max="99" placeholder="#" ' +
          'style="width:48px;padding:8px 6px;background:#111;border:1px solid #333;border-radius:6px;' +
          'color:#fff;font-size:13px;text-align:center;box-sizing:border-box">' +
        '<input id="np-nombre-' + inputId + '" type="text" placeholder="Nombre del jugador" ' +
          'style="flex:1;min-width:0;padding:8px 10px;background:#111;border:1px solid #333;' +
          'border-radius:6px;color:#fff;font-size:13px;box-sizing:border-box">' +
        '<button type="button" onclick="SRAdmin.addPlayer(\'' + partido.id + '\',\'' + faseId + '\',\'' + side + '\')" ' +
          'class="sr-q-btn sr-q-btn-dark" style="padding:8px 12px;font-size:12px;border-color:#555;white-space:nowrap">' +
          '+ Agregar' +
        '</button>' +
      '</div>' +
    '</div>';
  },

  async addPlayer(matchId, faseId, side) {
    const inputId = side + '-' + matchId;
    const dorsalInp = document.getElementById('np-dorsal-' + inputId);
    const nombreInp = document.getElementById('np-nombre-' + inputId);
    const nombre = nombreInp ? nombreInp.value.trim() : '';
    const dorsal = dorsalInp ? dorsalInp.value.trim() : '';
    if (!nombre) return this.toast('Ingresa el nombre del jugador', 'error');

    try {
      let rejected = false;
      await this.mutateMatch(faseId, matchId, match => {
        const team = side === 'local' ? match.local : match.visitante;
        if (!team.jugadores) team.jugadores = [];
        if (team.jugadores.some(j => j.nombre.toLowerCase() === nombre.toLowerCase())) { rejected = true; return; }
        team.jugadores.push({ nombre, dorsal: dorsal || '?' });
      });
      if (rejected) return this.toast('Ese jugador ya está en la lista', 'error');
      this.toast('✅ Jugador agregado: ' + nombre, 'success');
      this.renderEquipos();
    } catch (err) {
      if (this.isSessionError(err)) return this.handleSessionExpired();
      this.toast('Error al guardar. Verifica tu conexión.', 'error');
    }
  },

  async removePlayer(matchId, faseId, side, index) {
    try {
      let removedName = '';
      await this.mutateMatch(faseId, matchId, match => {
        const team = side === 'local' ? match.local : match.visitante;
        if (!team.jugadores || !team.jugadores[index]) return;
        removedName = team.jugadores[index].nombre;
        team.jugadores.splice(index, 1);
      });
      this.toast('Jugador eliminado: ' + removedName, 'info');
      this.renderEquipos();
    } catch (err) {
      if (this.isSessionError(err)) return this.handleSessionExpired();
      this.toast('Error. Verifica tu conexión.', 'error');
    }
  },

  async saveTeams(e, matchId, faseId) {
    e.preventDefault();
    const form = e.target;
    const localBandera = form.querySelector('[name="localBandera"]').value.trim() || '🏳️';
    const localNombre  = form.querySelector('[name="localNombre"]').value.trim() || 'Por definir';
    const visitBandera = form.querySelector('[name="visitBandera"]').value.trim() || '🏳️';
    const visitNombre  = form.querySelector('[name="visitNombre"]').value.trim() || 'Por definir';

    try {
      await this.mutateMatch(faseId, matchId, match => {
        match.local.bandera     = localBandera;
        match.local.nombre      = localNombre;
        match.visitante.bandera = visitBandera;
        match.visitante.nombre  = visitNombre;
        match.estado = 'pendiente';
      });
      this.toast('✅ Equipos actualizados', 'success');
    } catch (err) {
      if (this.isSessionError(err)) return this.handleSessionExpired();
      this.toast('Error al guardar. Verifica tu conexión.', 'error');
    }
  },

  // ── TAB: USUARIOS ───────────────────────────────────────

  async renderUsuarios() {
    const container = document.getElementById('sr-admin-usuarios-content');
    if (!container) return;
    container.innerHTML = '<p style="color:#666">Cargando…</p>';

    const [{ matches, results, points }, { users, predictions }] = await Promise.all([
      this.getConfig(), this.getUsersWithPredictions()
    ]);

    if (users.length === 0) {
      container.innerHTML = '<div class="sr-q-empty"><p>No hay participantes registrados aún.</p></div>';
      return;
    }

    const fasePuntos = this._resolvePoints(points);
    const racha = this._resolveRacha(points);

    // Orden cronológico real: por fase (en orden del torneo) y luego por número de partido.
    // Debe coincidir exactamente con QuinielaApp.calculatePointsFor() en sr-quiniela.js,
    // incluyendo el bono de racha, para que el admin vea los mismos puntos que el ranking público.
    const orderedMatchIds = [];
    this.fases.forEach(fase => {
      const partidos = (matches[fase.id] || []).slice().sort((a, b) => a.num - b.num);
      partidos.forEach(p => orderedMatchIds.push(p.id));
    });

    const ranked = users.map(u => {
      const preds = predictions[u.id] || {};
      let pts = 0, aciertos = 0, streak = 0;
      orderedMatchIds.forEach(mid => {
        const res = results[mid];
        if (!res) return;
        const pred = preds[mid];
        if (!pred) { streak = 0; return; } // sin predicción en partido finalizado = racha rota
        const fp = fasePuntos[pred.fase || res.faseId] || fasePuntos.dieciseisavos;
        // Use Number() coercion: mirrors calcMatchPoints fix in sr-quiniela.js
        const pL = Number(pred.localScore), pA = Number(pred.awayScore);
        const rL = Number(res.local),        rA = Number(res.visitante);
        const pr = pL > pA ? 'H' : pL < pA ? 'A' : 'D';
        const rr = rL > rA ? 'H' : rL < rA ? 'A' : 'D';
        if (pr === rr) {
          aciertos++;
          pts += fp.resultado;
          if (pL === rL && pA === rA) pts += fp.exacto;
          const resScorers = res.scorers || (res.scorer ? [res.scorer] : []);
          if (pred.scorer && resScorers.length > 0 && resScorers.includes(pred.scorer)) pts += fp.anotador;
          streak++;
          if (streak === 3) pts += racha.streak3;
          if (streak === 5) pts += racha.streak5;
          if (streak === 8) pts += racha.streak8;
        } else {
          streak = 0;
        }
      });
      return { ...u, pts, aciertos, predsCount: Object.keys(preds).length };
    }).sort((a, b) => b.pts - a.pts || b.predsCount - a.predsCount); // mismo desempate que el ranking público (QuinielaApp.getAllUsersWithStats)

    this._lastRanked = ranked;
    this._lastUserTotal = users.length;

    container.innerHTML =
      // ── Top controls ────────────────────────────────────
      '<div style="display:flex;gap:10px;align-items:center;margin-bottom:14px;flex-wrap:wrap">' +
        '<div style="flex:1;min-width:200px;position:relative">' +
          '<input type="search" id="sr-admin-user-search" ' +
            'placeholder="🔍  Buscar por usuario, nombre o email…" ' +
            'oninput="SRAdmin._filterUsers(this.value)" ' +
            'style="width:100%;padding:10px 14px;background:#181818;border:1px solid #2e2e2e;' +
              'border-radius:8px;color:#fff;font-size:13px;box-sizing:border-box;outline:none">' +
        '</div>' +
        '<select id="sr-admin-user-sort" onchange="SRAdmin._sortUsers(this.value)" ' +
          'style="padding:10px 12px;background:#181818;border:1px solid #2e2e2e;border-radius:8px;' +
            'color:#ccc;font-size:13px;flex-shrink:0">' +
          '<option value="pts">Ordenar: PTS (mayor a menor)</option>' +
          '<option value="nombre">Ordenar: Usuario (A-Z)</option>' +
          '<option value="reciente">Ordenar: Más reciente primero</option>' +
        '</select>' +
        '<span id="sr-admin-user-count" ' +
          'style="color:#555;font-size:13px;white-space:nowrap;flex-shrink:0">' +
          users.length + ' participantes' +
        '</span>' +
        '<button class="sr-q-btn sr-q-btn-dark" onclick="SRAdmin.exportData()" ' +
          'style="padding:8px 14px;font-size:12px;white-space:nowrap;flex-shrink:0">📥 EXPORTAR JSON</button>' +
      '</div>' +

      // ── Table ────────────────────────────────────────────
      '<div style="overflow-x:auto;border-radius:10px;border:1px solid #242424">' +
        '<table style="width:100%;border-collapse:collapse;font-size:14px">' +
          '<thead>' +
            '<tr style="background:#181818;border-bottom:1px solid #D4A017">' +
              this._userTH('#', 'center') +
              this._userTH('USUARIO') +
              this._userTH('EMAIL') +
              this._userTH('PTS', 'center') +
              this._userTH('PREDS', 'center') +
              this._userTH('ACCIONES', 'center') +
            '</tr>' +
          '</thead>' +
          '<tbody id="sr-admin-user-tbody">' + this._buildUserRows(ranked) + '</tbody>' +
        '</table>' +
        '<div id="sr-admin-user-empty" ' +
          'style="display:none;padding:32px;text-align:center;color:#555;font-size:14px">' +
          '🔍 Ningún usuario coincide con la búsqueda.' +
        '</div>' +
      '</div>';
  },

  _userTH(label, align) {
    return '<th style="padding:11px 14px;text-align:' + (align || 'left') + ';color:#D4A017;' +
      'font-family:\'Bebas Neue\',sans-serif;letter-spacing:2px;font-size:12px;' +
      'white-space:nowrap;font-weight:400">' + label + '</th>';
  },

  // Re-ordena la lista de participantes ya calculada (sin volver a leer Firestore)
  // y conserva el texto de búsqueda activo, si había alguno.
  _sortUsers(mode) {
    if (!this._lastRanked) return;
    const sorted = this._lastRanked.slice();
    if (mode === 'nombre') {
      sorted.sort((a, b) => (a.username || '').localeCompare(b.username || ''));
    } else if (mode === 'reciente') {
      sorted.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
    } else {
      sorted.sort((a, b) => b.pts - a.pts || b.predsCount - a.predsCount);
    }
    const tbody = document.getElementById('sr-admin-user-tbody');
    if (tbody) tbody.innerHTML = this._buildUserRows(sorted);
    const search = document.getElementById('sr-admin-user-search');
    if (search && search.value) this._filterUsers(search.value);
  },

  _buildUserRows(ranked) {
    return ranked.map((u, i) => {
      // La posición/medalla siempre refleja el ranking REAL por puntos (this._lastRanked,
      // que nunca se reordena), independiente del orden en que se estén mostrando las
      // filas — así "Ordenar: Usuario (A-Z)" no hace que le salga 🥇 al primero alfabético.
      const realPos = (this._lastRanked || ranked).findIndex(x => x.id === u.id) + 1;
      const pos  = realPos || (i + 1);
      const medal = pos === 1 ? '🥇' : pos === 2 ? '🥈' : pos === 3 ? '🥉' : null;
      const pct   = Math.round((u.predsCount / 32) * 100);
      const barClr = pct >= 75 ? '#22c55e' : pct >= 40 ? '#f59e0b' : '#ef4444';
      const rowHighlight = pos === 1 ? 'background:#1d1a08' : pos <= 3 ? 'background:#171717' : '';
      const searchVal = escapeHTML(
        ((u.username || '') + ' ' + (u.nombre || '') + ' ' + (u.email || '') + ' ' + (u.seleccion || u.pais || '')).toLowerCase()
      );
      return (
        '<tr data-search="' + searchVal + '" style="border-bottom:1px solid #1e1e1e;' + rowHighlight + '">' +

        // # ─────────────────────────────────────────────────
        '<td style="padding:12px 10px;text-align:center;width:44px">' +
          (medal
            ? '<span style="font-size:20px">' + medal + '</span>'
            : '<span style="color:#3a3a3a;font-family:\'Bebas Neue\',sans-serif;font-size:16px">' + pos + '</span>') +
        '</td>' +

        // USUARIO ─────────────────────────────────────────
        '<td style="padding:12px 14px">' +
          '<div style="font-weight:700;color:#f0f0f0;font-size:14px;letter-spacing:0.3px">' +
            '@' + escapeHTML(u.username) +
          '</div>' +
          '<div style="font-size:11px;color:#555;margin-top:3px;line-height:1.4">' +
            escapeHTML(u.nombre || '') +
            (u.seleccion || u.pais
              ? ' &nbsp;·&nbsp; <span style="color:#4a4a4a">' + escapeHTML(u.seleccion || u.pais || '') + '</span>'
              : '') +
          '</div>' +
        '</td>' +

        // EMAIL ───────────────────────────────────────────
        '<td style="padding:12px 14px;max-width:220px">' +
          '<span style="font-size:12px;color:#4a4a4a;word-break:break-all">' + escapeHTML(u.email || '') + '</span>' +
        '</td>' +

        // PTS ─────────────────────────────────────────────
        '<td style="padding:12px 10px;text-align:center;width:76px">' +
          '<span style="background:#D4A017;color:#111;padding:4px 11px;border-radius:20px;' +
            'font-family:\'Bebas Neue\',sans-serif;font-size:16px;font-weight:700;white-space:nowrap">' +
            u.pts +
          '</span>' +
        '</td>' +

        // PREDS ───────────────────────────────────────────
        '<td style="padding:12px 14px;text-align:center;width:100px">' +
          '<div style="font-size:12px;color:#888;margin-bottom:5px">' +
            '<strong style="color:#ccc">' + u.predsCount + '</strong>' +
            '<span style="color:#3a3a3a">/32</span>' +
          '</div>' +
          '<div style="background:#242424;border-radius:4px;height:4px;overflow:hidden">' +
            '<div style="height:100%;width:' + pct + '%;background:' + barClr + ';border-radius:4px"></div>' +
          '</div>' +
        '</td>' +

        // ACCIONES ────────────────────────────────────────
        '<td style="padding:10px 12px;width:132px">' +
          '<div style="display:flex;gap:5px;justify-content:center;align-items:center">' +
            '<button onclick="SRAdmin.showUserPredictions(\'' + u.id + '\')" ' +
              'title="Ver / editar vaticinios" ' +
              'style="background:#16163a;border:1px solid #4f46e5;color:#818cf8;' +
                'padding:6px 9px;border-radius:6px;cursor:pointer;font-size:12px;' +
                'font-weight:600;white-space:nowrap">📋 Ver</button>' +
            '<button onclick="SRAdmin.resetPassword(\'' + u.id + '\')" ' +
              'title="Enviar email de reset de contraseña" ' +
              'style="background:#0a1528;border:1px solid #1d4ed8;color:#60a5fa;' +
                'padding:6px 8px;border-radius:6px;cursor:pointer;font-size:13px">📧</button>' +
            '<button onclick="SRAdmin.deleteUser(\'' + u.id + '\')" ' +
              'title="Eliminar participante" ' +
              'style="background:#1a0808;border:1px solid #991b1b;color:#f87171;' +
                'padding:6px 8px;border-radius:6px;cursor:pointer;font-size:13px">🗑️</button>' +
          '</div>' +
        '</td>' +

        '</tr>'
      );
    }).join('');
  },

  // ── VISOR / EDITOR DE VATICINIOS ────────────────────────
  // Abre un modal con TODAS las predicciones de un usuario, organizadas por
  // fase, mostrando el resultado oficial (si ya existe) y los puntos ganados.
  // Cada predicción tiene un formulario inline para editar y guardar. Todos
  // los writes usan merge:true → NUNCA se borra ningún vaticio existente.
  async showUserPredictions(userId) {
    const existing = document.getElementById('sr-admin-preds-modal');
    if (existing) existing.remove();

    // Overlay
    const modal = document.createElement('div');
    modal.id = 'sr-admin-preds-modal';
    modal.style.cssText = 'position:fixed;top:0;left:0;right:0;bottom:0;' +
      'background:rgba(0,0,0,0.88);z-index:9999;overflow-y:auto;padding:16px;box-sizing:border-box';
    modal.addEventListener('click', e => { if (e.target === modal) SRAdmin._closePredModal(); });

    const box = document.createElement('div');
    box.style.cssText = 'max-width:960px;margin:0 auto;background:#111;border-radius:12px;padding:24px;';
    box.innerHTML = '<p style="color:#888;padding:40px 0;text-align:center">Cargando predicciones…</p>';
    modal.appendChild(box);
    document.body.appendChild(modal);

    try {
      const [userDoc, predsDoc, { matches, results, points }] = await Promise.all([
        this.db.collection('users').doc(userId).get(),
        this.db.collection('predictions').doc(userId).get(),
        this.getConfig()
      ]);

      if (!userDoc.exists) {
        box.innerHTML = '<p style="color:#ef4444;padding:20px">Usuario no encontrado.</p>' +
          this._predModalCloseBtn();
        return;
      }

      const user    = userDoc.data();
      const preds   = predsDoc.exists ? predsDoc.data() : {};
      const uname   = escapeHTML(user.username || userId);
      const predCnt = Object.keys(preds).length;
      const FA      = this._resolvePoints(points);

      let body = '';

      this.fases.forEach(fase => {
        const partidos = (matches[fase.id] || []).slice().sort((a, b) => a.num - b.num);
        if (partidos.length === 0) return;

        body += '<div style="font-family:\'Bebas Neue\',sans-serif;font-size:17px;color:#D4A017;' +
          'letter-spacing:2px;margin:24px 0 10px;padding-bottom:6px;border-bottom:1px solid #2a2a2a">' +
          fase.icono + ' ' + fase.nombre + '</div>';

        partidos.forEach(p => {
          const pred   = preds[p.id]   || null;
          const result = results[p.id] || null;
          const fp     = FA[fase.id]   || FA.dieciseisavos;

          // Points earned for this prediction
          let ptsEarned = null;
          if (pred && result) {
            const pL = Number(pred.localScore), pA = Number(pred.awayScore);
            const rL = Number(result.local),    rA = Number(result.visitante);
            const pr = pL > pA ? 'H' : pL < pA ? 'A' : 'D';
            const rr = rL > rA ? 'H' : rL < rA ? 'A' : 'D';
            ptsEarned = 0;
            if (pr === rr) {
              ptsEarned += fp.resultado;
              if (pL === rL && pA === rA) ptsEarned += fp.exacto;
              const rs = result.scorers || (result.scorer ? [result.scorer] : []);
              if (pred.scorer && rs.length > 0 && rs.includes(pred.scorer)) ptsEarned += fp.anotador;
            }
          }

          const hasPred   = !!pred;
          const borderClr = hasPred ? '#D4A017' : '#2a2a2a';

          body +=
            '<div style="background:#181818;border:1px solid ' + borderClr + ';border-radius:8px;' +
              'padding:14px 16px;margin-bottom:8px">' +

            // Row 1: match info + result badge
            '<div style="display:flex;justify-content:space-between;align-items:flex-start;' +
              'flex-wrap:wrap;gap:8px;margin-bottom:12px">' +
              '<div>' +
                '<span style="font-size:14px;font-weight:600;color:#fff">' +
                  escapeHTML(p.local.bandera) + ' ' + escapeHTML(p.local.nombre) +
                  ' <span style="color:#555;font-weight:400">vs</span> ' +
                  escapeHTML(p.visitante.nombre) + ' ' + escapeHTML(p.visitante.bandera) +
                '</span>' +
                '<div style="font-size:11px;color:#555;margin-top:2px">Partido ' + p.num + ' · ' + p.fecha + '</div>' +
              '</div>' +
              '<div style="display:flex;align-items:center;gap:8px;flex-shrink:0">' +
                (result
                  ? '<span style="font-family:\'Bebas Neue\',sans-serif;font-size:22px;color:#D4A017">' +
                      result.local + '–' + result.visitante + '</span>' +
                    (ptsEarned !== null
                      ? '<span style="padding:3px 10px;border-radius:20px;font-size:12px;font-weight:700;' +
                          'background:' + (ptsEarned > 0 ? '#0d2a0d' : '#2a0d0d') + ';' +
                          'color:' + (ptsEarned > 0 ? '#22c55e' : '#f87171') + '">' +
                          (ptsEarned > 0 ? '+' + ptsEarned + ' pts ✓' : '0 pts ✗') +
                        '</span>'
                      : '')
                  : '<span style="font-size:11px;color:#444;font-style:italic">Sin resultado</span>') +
              '</div>' +
            '</div>' +

            // Row 2: edit form
            '<form onsubmit="SRAdmin.editUserPrediction(event,\'' + userId + '\',\'' + p.id + '\',\'' + fase.id + '\')" ' +
              'style="display:flex;gap:8px;align-items:flex-end;flex-wrap:wrap">' +
              '<div>' +
                '<label style="font-size:10px;color:#555;display:block;margin-bottom:3px;letter-spacing:1px">' +
                  escapeHTML((p.local.nombre || 'LOCAL').toUpperCase().slice(0, 8)) + '</label>' +
                '<input type="number" name="localScore" min="0" max="20" required ' +
                  'value="' + (hasPred ? Number(pred.localScore) : '') + '" placeholder="–" ' +
                  'style="width:56px;padding:8px 4px;background:#0d0d0d;border:1px solid ' + (hasPred ? '#D4A017' : '#333') + ';' +
                    'border-radius:6px;color:#fff;font-family:\'Bebas Neue\',sans-serif;font-size:20px;' +
                    'text-align:center;box-sizing:border-box">' +
              '</div>' +
              '<span style="padding-bottom:9px;color:#555;font-size:18px;flex-shrink:0">—</span>' +
              '<div>' +
                '<label style="font-size:10px;color:#555;display:block;margin-bottom:3px;letter-spacing:1px">' +
                  escapeHTML((p.visitante.nombre || 'VISIT').toUpperCase().slice(0, 8)) + '</label>' +
                '<input type="number" name="awayScore" min="0" max="20" required ' +
                  'value="' + (hasPred ? Number(pred.awayScore) : '') + '" placeholder="–" ' +
                  'style="width:56px;padding:8px 4px;background:#0d0d0d;border:1px solid ' + (hasPred ? '#D4A017' : '#333') + ';' +
                    'border-radius:6px;color:#fff;font-family:\'Bebas Neue\',sans-serif;font-size:20px;' +
                    'text-align:center;box-sizing:border-box">' +
              '</div>' +
              '<div>' +
                '<label style="font-size:10px;color:#555;display:block;margin-bottom:3px;letter-spacing:1px">ANOTADOR</label>' +
                '<select name="scorer" ' +
                  'style="padding:9px 8px;background:#0d0d0d;border:1px solid #333;border-radius:6px;' +
                    'color:#fff;font-size:13px;box-sizing:border-box;max-width:180px">' +
                  this.scorerOptions(p, pred && pred.scorer) +
                '</select>' +
              '</div>' +
              '<button type="submit" ' +
                'style="padding:9px 16px;border-radius:6px;cursor:pointer;font-size:13px;font-weight:700;' +
                  'white-space:nowrap;border:1px solid ' + (hasPred ? '#22c55e' : '#60a5fa') + ';' +
                  'background:' + (hasPred ? '#0d2a0d' : '#0d1a2a') + ';' +
                  'color:' + (hasPred ? '#22c55e' : '#60a5fa') + '">' +
                (hasPred ? '💾 Guardar cambio' : '✏️ Crear vaticinio') +
              '</button>' +
              (hasPred
                ? '<span style="font-size:11px;color:#444;align-self:flex-end;padding-bottom:4px">' +
                    (pred.savedAt ? pred.savedAt.slice(0, 10) : '') +
                    (pred.editedByAdmin ? ' · <span style="color:#f59e0b">✏️ editado</span>' : '') +
                  '</span>'
                : '<span style="font-size:11px;color:#444;align-self:flex-end;padding-bottom:4px">Sin vaticinio</span>') +
            '</form>' +

            '</div>';
        });
      });

      box.innerHTML =
        // Header
        '<div style="display:flex;justify-content:space-between;align-items:flex-start;' +
          'flex-wrap:wrap;gap:12px;margin-bottom:20px;padding-bottom:16px;border-bottom:1px solid #2a2a2a">' +
          '<div>' +
            '<div style="font-family:\'Bebas Neue\',sans-serif;font-size:26px;color:#D4A017;letter-spacing:2px">' +
              'VATICINIOS — @' + uname +
            '</div>' +
            '<div style="font-size:13px;color:#666;margin-top:4px">' +
              escapeHTML(user.nombre || '') +
              (user.email ? ' · ' + escapeHTML(user.email) : '') +
              ' · <strong style="color:#aaa">' + predCnt + ' predicciones guardadas</strong>' +
            '</div>' +
          '</div>' +
          this._predModalCloseBtn() +
        '</div>' +
        // Phase sections
        body +
        // Footer
        '<div style="margin-top:24px;padding-top:16px;border-top:1px solid #2a2a2a;text-align:right">' +
          this._predModalCloseBtn() +
        '</div>';

    } catch (err) {
      if (this.isSessionError(err)) { this._closePredModal(); return this.handleSessionExpired(); }
      box.innerHTML = '<p style="color:#ef4444;padding:20px">Error al cargar las predicciones. Verifica tu conexión.</p>' +
        this._predModalCloseBtn();
    }
  },

  // Guarda UN vaticinio de un usuario. Siempre usa merge:true — nunca borra
  // los vaticinios de otros partidos del mismo usuario.
  async editUserPrediction(e, userId, matchId, faseId) {
    e.preventDefault();
    const form = e.target;
    const localScore = parseInt(form.querySelector('[name="localScore"]').value, 10);
    const awayScore  = parseInt(form.querySelector('[name="awayScore"]').value,  10);
    const scorer     = form.querySelector('[name="scorer"]').value.trim() || null;

    if (isNaN(localScore) || isNaN(awayScore)) {
      return this.toast('Ingresa marcadores válidos', 'error');
    }
    if (localScore < 0 || localScore > 20 || awayScore < 0 || awayScore > 20) {
      return this.toast('El marcador debe estar entre 0 y 20', 'error');
    }

    const btn = form.querySelector('button[type="submit"]');
    if (btn) { btn.disabled = true; btn.textContent = 'Guardando…'; }

    try {
      await this.db.collection('predictions').doc(userId).set(
        { [matchId]: {
            localScore, awayScore,
            scorer: scorer || null,
            fase: faseId,
            savedAt: new Date().toISOString(),
            editedByAdmin: true
          }
        },
        { merge: true }
      );
      this.toast('✅ Vaticinio actualizado correctamente', 'success');
      // Refresh modal in-place (re-reads from Firestore for accuracy)
      await this.showUserPredictions(userId);
      // La columna PTS de la tabla detrás del modal quedaba desactualizada hasta
      // cambiar de pestaña y volver — la refrescamos también aquí.
      this.renderUsuarios();
    } catch (err) {
      if (btn) { btn.disabled = false; btn.textContent = '💾 Guardar cambio'; }
      if (this.isSessionError(err)) return this.handleSessionExpired();
      this.toast('Error al guardar. Verifica tu conexión.', 'error');
    }
  },

  // Filtra las filas de la tabla de usuarios según el texto de búsqueda.
  // Opera solo sobre el DOM ya renderizado — sin ninguna lectura a Firestore.
  _filterUsers(query) {
    const q = (query || '').toLowerCase().trim();
    const rows = document.querySelectorAll('#sr-admin-user-tbody tr[data-search]');
    let visible = 0;
    rows.forEach(row => {
      const match = !q || row.dataset.search.includes(q);
      row.style.display = match ? '' : 'none';
      if (match) visible++;
    });
    const countEl  = document.getElementById('sr-admin-user-count');
    const emptyEl  = document.getElementById('sr-admin-user-empty');
    if (countEl) countEl.textContent = q
      ? visible + ' de ' + rows.length + ' participantes'
      : rows.length + ' participantes';
    if (emptyEl) emptyEl.style.display = (visible === 0) ? 'block' : 'none';
  },

  _closePredModal() {
    const m = document.getElementById('sr-admin-preds-modal');
    if (m) m.remove();
  },

  _predModalCloseBtn() {
    return '<button onclick="SRAdmin._closePredModal()" ' +
      'style="background:#2a2a2a;color:#ccc;border:1px solid #444;padding:9px 18px;' +
        'border-radius:8px;cursor:pointer;font-size:13px;flex-shrink:0">✕ Cerrar</button>';
  },

  // Firebase Auth (cliente) no permite que un admin establezca directamente
  // la contraseña de OTRO usuario — esa capacidad requiere el Admin SDK
  // (backend). El equivalente correcto y seguro es enviarle un correo real
  // de restablecimiento, igual que el botón "¿Olvidaste tu contraseña?".
  async resetPassword(userId) {
    try {
      const doc = await this.db.collection('users').doc(userId).get();
      if (!doc.exists) return this.toast('Usuario no encontrado', 'error');
      const user = doc.data();
      if (!confirm('¿Enviar correo de restablecimiento de contraseña a @' + user.username + ' (' + user.email + ')?')) return;
      await this.auth.sendPasswordResetEmail(user.email);
      this.toast('📧 Correo de restablecimiento enviado a ' + user.email, 'success');
    } catch (err) {
      if (this.isSessionError(err)) return this.handleSessionExpired();
      this.toast('Error al enviar el correo. Verifica tu conexión.', 'error');
    }
  },

  // Elimina el perfil/predicciones/mapeo de username de Firestore. La cuenta
  // de inicio de sesión en Firebase Authentication NO se borra desde aquí
  // (requiere el Admin SDK) — si quieres liberar ese email por completo,
  // bórrala manualmente desde la consola de Firebase → Authentication.
  async deleteUser(userId) {
    if (!confirm('¿Eliminar este usuario y todas sus predicciones de la quiniela? Su cuenta de acceso (login) seguirá existiendo en Firebase Authentication — bórrala ahí manualmente si quieres liberar ese email por completo.')) return;
    try {
      const doc = await this.db.collection('users').doc(userId).get();
      const usernameLower = doc.exists ? doc.data().usernameLower : null;
      const writes = [
        this.db.collection('users').doc(userId).delete(),
        this.db.collection('predictions').doc(userId).delete(),
        this._cleanupUserGroups(userId)
      ];
      if (usernameLower) writes.push(this.db.collection('usernames').doc(usernameLower).delete());
      await Promise.all(writes);
      this.toast('Usuario eliminado de la quiniela', 'warning');
      // El contador público se recalcula automáticamente en renderStats() abajo.
      this.renderUsuarios();
      this.renderStats();
    } catch (err) {
      if (this.isSessionError(err)) return this.handleSessionExpired();
      this.toast('Error al eliminar. Verifica tu conexión.', 'error');
    }
  },

  // Sin esto, eliminar un usuario dejaba su membresía en "grupos/{code}.members"
  // huérfana para siempre (el grupo seguía "viéndolo" como miembro activo).
  // Si era el único miembro, el grupo entero queda vacío/inútil → se borra.
  // Si quedan otros miembros, solo se remueve su entrada — el grupo sigue intacto.
  async _cleanupUserGroups(userId) {
    try {
      const gruposSnap = await this.db.collection('grupos').get();
      const writes = [];
      gruposSnap.forEach(doc => {
        const g = doc.data();
        if (!g.members || !g.members[userId]) return;
        const memberIds = Object.keys(g.members).filter(id => g.members[id]);
        const isOnlyMember = memberIds.length === 1 && memberIds[0] === userId;
        if (isOnlyMember) {
          writes.push(this.db.collection('grupos').doc(doc.id).delete());
        } else {
          writes.push(this.db.collection('grupos').doc(doc.id).update({
            ['members.' + userId]: firebase.firestore.FieldValue.delete()
          }));
        }
      });
      if (writes.length) await Promise.all(writes);
    } catch (e) { /* no crítico — no debe bloquear la eliminación del usuario */ }
  },

  // ── PATROCINADORES: helper compartido de subida de logo ─
  // Un solo lugar para procesar cualquier logo de sponsor (Título, Banner
  // rotativo o por premio) — evita repetir la misma lógica 3 veces. Rechaza
  // archivos pesados, redimensiona a máx. 800×300 vía canvas, y ADEMÁS valida
  // el tamaño del resultado ya codificado en base64 (algo que no se hacía en
  // la versión de Champions) para dejar margen bajo el límite de 1MiB por
  // documento de Firestore.
  _sponsorLogoCache: {},

  processSponsorLogo(file, key, onReady) {
    if (!file) return;
    if (file.size > 500 * 1024) {
      this.toast('La imagen no debe superar 500KB', 'error');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const maxW = 800, maxH = 300;
        const ratio = Math.min(maxW / img.width, maxH / img.height, 1);
        const w = Math.max(1, Math.round(img.width * ratio));
        const h = Math.max(1, Math.round(img.height * ratio));
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        const dataUrl = canvas.toDataURL('image/png');
        if (dataUrl.length > 700 * 1024) {
          this.toast('La imagen sigue siendo muy pesada después de procesarla. Prueba con un archivo más simple.', 'error');
          return;
        }
        this._sponsorLogoCache[key] = dataUrl;
        onReady(dataUrl);
      };
      img.onerror = () => this.toast('No se pudo leer la imagen', 'error');
      img.src = reader.result;
    };
    reader.onerror = () => this.toast('No se pudo leer el archivo', 'error');
    reader.readAsDataURL(file);
  },

  handleSponsorLogoInput(e, key, previewId) {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    this.processSponsorLogo(file, key, dataUrl => {
      const img = document.getElementById(previewId);
      if (img) { img.src = dataUrl; img.style.display = 'inline-block'; }
    });
  },

  // ── TAB: PUNTOS ─────────────────────────────────────────

  async renderPuntos() {
    const container = document.getElementById('sr-admin-puntos-content');
    if (!container) return;
    container.innerHTML = '<p style="color:#666">Cargando…</p>';

    const { points } = await this.getConfig();
    const resolved = this._resolvePoints(points);
    const racha = this._resolveRacha(points);

    container.innerHTML =
      '<form id="sr-admin-puntos-form" onsubmit="SRAdmin.savePuntos(event)">' +
        this.fases.map(fase => {
          const fp = resolved[fase.id];
          const field = (key, label) =>
            '<div>' +
              '<label style="font-size:11px;color:#666;display:block;margin-bottom:4px">' + label + '</label>' +
              '<input type="number" min="0" max="200" name="pts-' + fase.id + '-' + key + '" value="' + fp[key] + '" ' +
                'style="width:100%;padding:9px 10px;background:#111;border:1px solid #333;border-radius:6px;' +
                  'color:#fff;font-family:\'Bebas Neue\',sans-serif;font-size:18px;text-align:center;box-sizing:border-box">' +
            '</div>';
          return '<div class="sr-q-admin-card" style="margin-bottom:12px">' +
            '<div style="display:flex;align-items:center;gap:10px;margin-bottom:12px">' +
              '<span style="font-size:22px">' + fase.icono + '</span>' +
              '<span style="font-family:\'Bebas Neue\',sans-serif;color:#D4A017;letter-spacing:2px;font-size:16px">' + fase.nombre + '</span>' +
            '</div>' +
            '<div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:10px">' +
              field('resultado', 'RESULTADO CORRECTO') +
              field('exacto', 'MARCADOR EXACTO') +
              field('anotador', 'ANOTADOR ESTRELLA') +
            '</div>' +
          '</div>';
        }).join('') +
        '<div class="sr-q-admin-card" style="margin-bottom:12px;border-color:#D4A017">' +
          '<div style="font-family:\'Bebas Neue\',sans-serif;color:#D4A017;letter-spacing:2px;font-size:16px;margin-bottom:12px">' +
            '🔥 BONUS RACHA' +
          '</div>' +
          '<div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:10px">' +
            '<div>' +
              '<label style="font-size:11px;color:#666;display:block;margin-bottom:4px">A LOS 3 ACIERTOS SEGUIDOS</label>' +
              '<input type="number" min="0" max="200" name="racha-streak3" value="' + racha.streak3 + '" ' +
                'style="width:100%;padding:9px 10px;background:#111;border:1px solid #333;border-radius:6px;' +
                  'color:#fff;font-family:\'Bebas Neue\',sans-serif;font-size:18px;text-align:center;box-sizing:border-box">' +
            '</div>' +
            '<div>' +
              '<label style="font-size:11px;color:#666;display:block;margin-bottom:4px">A LOS 5 ACIERTOS SEGUIDOS</label>' +
              '<input type="number" min="0" max="200" name="racha-streak5" value="' + racha.streak5 + '" ' +
                'style="width:100%;padding:9px 10px;background:#111;border:1px solid #333;border-radius:6px;' +
                  'color:#fff;font-family:\'Bebas Neue\',sans-serif;font-size:18px;text-align:center;box-sizing:border-box">' +
            '</div>' +
            '<div>' +
              '<label style="font-size:11px;color:#666;display:block;margin-bottom:4px">A LOS 8 ACIERTOS SEGUIDOS</label>' +
              '<input type="number" min="0" max="200" name="racha-streak8" value="' + racha.streak8 + '" ' +
                'style="width:100%;padding:9px 10px;background:#111;border:1px solid #333;border-radius:6px;' +
                  'color:#fff;font-family:\'Bebas Neue\',sans-serif;font-size:18px;text-align:center;box-sizing:border-box">' +
            '</div>' +
          '</div>' +
        '</div>' +
        '<button type="submit" class="sr-q-btn sr-q-btn-primary" style="margin-top:8px">💾 GUARDAR PUNTOS</button>' +
      '</form>';
  },

  async savePuntos(e) {
    e.preventDefault();
    const form = e.target;
    const num = name => {
      const v = parseInt(form.querySelector('[name="' + name + '"]').value, 10);
      return isNaN(v) ? 0 : Math.max(0, Math.min(200, v));
    };
    const points = { racha: {
      streak3: num('racha-streak3'), streak5: num('racha-streak5'), streak8: num('racha-streak8')
    } };
    this.fases.forEach(fase => {
      points[fase.id] = {
        resultado: num('pts-' + fase.id + '-resultado'),
        exacto:    num('pts-' + fase.id + '-exacto'),
        anotador:  num('pts-' + fase.id + '-anotador')
      };
    });
    try {
      await this.db.collection('quiniela_config').doc('points').set(points);
      this.toast('✅ Puntos actualizados. El ranking y las estadísticas se recalculan con los nuevos valores.', 'success');
    } catch (err) {
      if (this.isSessionError(err)) return this.handleSessionExpired();
      this.toast('Error al guardar. Verifica tu conexión.', 'error');
    }
  },

  // ── TAB: PREMIOS ────────────────────────────────────────

  // Normaliza un premio a { texto, sponsorName, sponsorLogo, sponsorUrl } — los
  // premios guardados antes de este cambio son strings planos; se tratan como
  // { texto: elString } sin necesitar ninguna migración manual.
  _normalizePremio(raw) {
    if (typeof raw === 'string') return { texto: raw, sponsorName: '', sponsorLogo: '', sponsorUrl: '' };
    return Object.assign({ texto: '', sponsorName: '', sponsorLogo: '', sponsorUrl: '' }, raw || {});
  },

  async renderPremios() {
    const container = document.getElementById('sr-admin-premios-content');
    if (!container) return;
    container.innerHTML = '<p style="color:#666">Cargando…</p>';

    const { admin } = await this.getConfig();
    const defaults = {
      primero: 'Camisola oficial a elección, personalizada (Q350 de valor)',
      segundo: '50% descuento en cualquier camisola',
      tercero: '25% descuento en cualquier camisola',
      top10:   'Envío gratis en tu próximo pedido de Soccer Revolution',
      exacto:  'Cupón de 10% por cada marcador exacto que aciertes'
    };
    const premios = admin.premios || {};

    const items = [
      { key: 'primero', label: '🥇 1ER LUGAR', placeholder: 'Premio al primer lugar' },
      { key: 'segundo', label: '🥈 2DO LUGAR', placeholder: 'Premio al segundo lugar' },
      { key: 'tercero', label: '🥉 3ER LUGAR', placeholder: 'Premio al tercer lugar' },
      { key: 'top10',   label: '🎖️ TOP 10',    placeholder: 'Premio para el top 10' },
      { key: 'exacto',  label: '⚽ MARCADOR EXACTO', placeholder: 'Premio por marcador exacto' }
    ];

    container.innerHTML =
      '<form id="sr-admin-premios-form" onsubmit="SRAdmin.savePremios(event)">' +
        items.map(item => {
          const p = this._normalizePremio(premios[item.key] !== undefined ? premios[item.key] : defaults[item.key]);
          const logoKey = 'premio-' + item.key;
          const previewId = 'premio-logo-preview-' + item.key;
          return '<div class="sr-q-admin-card" style="margin-bottom:16px">' +
            '<label style="display:block;font-size:12px;color:#888;letter-spacing:2px;margin-bottom:6px">' +
              item.label + '</label>' +
            '<input name="' + item.key + '-texto" value="' + escapeHTML(p.texto || '') + '" ' +
              'placeholder="' + item.placeholder + '" ' +
              'style="width:100%;padding:12px 14px;background:#111;border:1px solid #333;' +
              'border-radius:8px;color:#fff;font-size:14px;box-sizing:border-box;margin-bottom:12px">' +
            '<div style="border-top:1px solid #2a2a2a;padding-top:12px">' +
              '<label style="font-size:11px;color:#555;letter-spacing:1px;display:block;margin-bottom:8px">' +
                '📢 SPONSOR DE ESTE PREMIO (opcional)</label>' +
              '<div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:10px">' +
                '<input name="' + item.key + '-sponsorName" value="' + escapeHTML(p.sponsorName || '') + '" ' +
                  'placeholder="Nombre del patrocinador" ' +
                  'style="padding:9px 10px;background:#111;border:1px solid #333;border-radius:6px;color:#fff;font-size:13px;box-sizing:border-box">' +
                '<input name="' + item.key + '-sponsorUrl" value="' + escapeHTML(p.sponsorUrl || '') + '" ' +
                  'placeholder="https://sitio-del-sponsor.com" type="url" ' +
                  'style="padding:9px 10px;background:#111;border:1px solid #333;border-radius:6px;color:#fff;font-size:13px;box-sizing:border-box">' +
              '</div>' +
              '<div style="display:flex;align-items:center;gap:10px">' +
                '<img id="' + previewId + '" src="' + escapeHTML(p.sponsorLogo || '') + '" ' +
                  'style="height:32px;max-width:100px;object-fit:contain;background:#0d0d0d;border-radius:4px;' +
                  (p.sponsorLogo ? '' : 'display:none;') + '">' +
                '<input type="file" accept="image/png,image/jpeg,image/webp" ' +
                  'onchange="SRAdmin.handleSponsorLogoInput(event,\'' + logoKey + '\',\'' + previewId + '\')" ' +
                  'style="font-size:12px;color:#888;flex:1">' +
              '</div>' +
            '</div>' +
          '</div>';
        }).join('') +
        '<button type="submit" class="sr-q-btn sr-q-btn-primary" style="margin-top:8px">' +
          '💾 GUARDAR PREMIOS' +
        '</button>' +
      '</form>';
  },

  async savePremios(e) {
    e.preventDefault();
    const form = e.target;
    const keys = ['primero', 'segundo', 'tercero', 'top10', 'exacto'];
    const premios = {};
    keys.forEach(key => {
      const logoKey = 'premio-' + key;
      const cachedLogo = this._sponsorLogoCache[logoKey];
      const existingImg = document.getElementById('premio-logo-preview-' + key);
      premios[key] = {
        texto: form.querySelector('[name="' + key + '-texto"]').value.trim(),
        sponsorName: form.querySelector('[name="' + key + '-sponsorName"]').value.trim(),
        sponsorUrl: form.querySelector('[name="' + key + '-sponsorUrl"]').value.trim(),
        sponsorLogo: cachedLogo || (existingImg && existingImg.src.startsWith('data:') ? existingImg.src : '')
      };
    });
    try {
      await this.db.collection('quiniela_config').doc('admin').set({ premios }, { merge: true });
      keys.forEach(key => { delete this._sponsorLogoCache['premio-' + key]; });
      this.toast('✅ Premios actualizados. Los participantes verán los cambios.', 'success');
    } catch (err) {
      if (this.isSessionError(err)) return this.handleSessionExpired();
      this.toast('Error al guardar. Verifica tu conexión.', 'error');
    }
  },

  // ── TAB: PATROCINADORES ─────────────────────────────────
  // Colección quiniela_sponsors — un documento POR sponsor (no un array
  // compartido en un solo doc como en la versión de Champions) para que el
  // límite de 1MiB por documento de Firestore nunca dependa de cuántos
  // sponsors haya en total. tier: 'title' (uno solo, id fijo 'title') o
  // 'banner' (varios, franja rotativa visible en Inicio/Ranking/Premios/Mis Stats).

  async renderPatrocinadores() {
    const container = document.getElementById('sr-admin-patrocinadores-content');
    if (!container) return;
    container.innerHTML = '<p style="color:#666">Cargando…</p>';

    try {
      const snap = await this.db.collection('quiniela_sponsors').get();
      const all = [];
      snap.forEach(doc => all.push({ id: doc.id, ...doc.data() }));
      const title = all.find(s => s.tier === 'title') || null;
      const banners = all.filter(s => s.tier === 'banner').sort((a, b) => (a.order || 0) - (b.order || 0));

      container.innerHTML =
        '<div class="sr-q-admin-card-title" style="margin-top:0">🏆 PATROCINADOR OFICIAL</div>' +
        '<p style="color:#666;font-size:13px;margin:0 0 14px">Banner destacado, máxima visibilidad, arriba del todo en Inicio.</p>' +
        this._sponsorTitleForm(title) +

        '<div class="sr-q-admin-card-title" style="margin-top:36px">📢 BANNER ROTATIVO</div>' +
        '<p style="color:#666;font-size:13px;margin:0 0 14px">Franja de logos visible en Inicio, Ranking, Premios y Mis Stats.</p>' +
        this._sponsorBannerNewForm() +
        (banners.length
          ? banners.map((s, i) => this._sponsorBannerRow(s, i, banners.length)).join('')
          : '<p style="color:#444;font-size:13px;margin-top:12px">Aún no hay sponsors en el banner rotativo.</p>');

    } catch (err) {
      if (this.isSessionError(err)) return this.handleSessionExpired();
      container.innerHTML = '<p style="color:#ef4444">Error al cargar patrocinadores. Verifica tu conexión.</p>';
    }
  },

  _sponsorTitleForm(title) {
    const t = title || { name: '', url: '', logo: '' };
    return '<div class="sr-q-admin-card">' +
      '<form onsubmit="SRAdmin.saveSponsorTitle(event)">' +
        '<div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:10px">' +
          '<input name="name" value="' + escapeHTML(t.name || '') + '" placeholder="Nombre del patrocinador" required ' +
            'style="padding:10px 12px;background:#111;border:1px solid #333;border-radius:6px;color:#fff;font-size:14px;box-sizing:border-box">' +
          '<input name="url" value="' + escapeHTML(t.url || '') + '" placeholder="https://sitio-del-sponsor.com" type="url" ' +
            'style="padding:10px 12px;background:#111;border:1px solid #333;border-radius:6px;color:#fff;font-size:14px;box-sizing:border-box">' +
        '</div>' +
        '<div style="display:flex;align-items:center;gap:12px;margin-bottom:12px">' +
          '<img id="sponsor-title-preview" src="' + escapeHTML(t.logo || '') + '" ' +
            'style="height:40px;max-width:140px;object-fit:contain;background:#0d0d0d;border-radius:4px;' +
            (t.logo ? '' : 'display:none;') + '">' +
          '<input type="file" accept="image/png,image/jpeg,image/webp" ' +
            'onchange="SRAdmin.handleSponsorLogoInput(event,\'title\',\'sponsor-title-preview\')" ' +
            'style="font-size:12px;color:#888;flex:1">' +
        '</div>' +
        '<div style="display:flex;gap:10px">' +
          '<button type="submit" class="sr-q-btn sr-q-btn-primary" style="padding:9px 18px;font-size:13px">💾 GUARDAR</button>' +
          (title
            ? '<button type="button" onclick="SRAdmin.removeSponsorTitle()" class="sr-q-btn sr-q-btn-dark" ' +
                'style="padding:9px 16px;font-size:13px;border-color:#ef4444;color:#ef4444">🗑️ Quitar</button>'
            : '') +
          (title
            ? '<span style="align-self:center;font-size:12px;color:#555;margin-left:auto">👆 ' + (title.clicks || 0) + ' clics</span>'
            : '') +
        '</div>' +
      '</form>' +
    '</div>';
  },

  async saveSponsorTitle(e) {
    e.preventDefault();
    const form = e.target;
    const name = form.querySelector('[name="name"]').value.trim();
    const url = form.querySelector('[name="url"]').value.trim();
    if (!name) return this.toast('Ingresa el nombre del patrocinador', 'error');
    const preview = document.getElementById('sponsor-title-preview');
    const logo = this._sponsorLogoCache['title'] || (preview && preview.src.startsWith('data:') ? preview.src : '');
    try {
      const ref = this.db.collection('quiniela_sponsors').doc('title');
      const existing = await ref.get();
      await ref.set({
        name, url, logo, tier: 'title', active: true,
        clicks: existing.exists ? (existing.data().clicks || 0) : 0,
        createdAt: existing.exists ? existing.data().createdAt : new Date().toISOString()
      });
      delete this._sponsorLogoCache['title'];
      this.toast('✅ Patrocinador Oficial guardado', 'success');
      this.renderPatrocinadores();
    } catch (err) {
      if (this.isSessionError(err)) return this.handleSessionExpired();
      this.toast('Error al guardar. Verifica tu conexión.', 'error');
    }
  },

  async removeSponsorTitle() {
    if (!confirm('¿Quitar el Patrocinador Oficial? Ya no se mostrará en el Inicio de los participantes.')) return;
    try {
      await this.db.collection('quiniela_sponsors').doc('title').delete();
      this.toast('Patrocinador Oficial eliminado', 'info');
      this.renderPatrocinadores();
    } catch (err) {
      if (this.isSessionError(err)) return this.handleSessionExpired();
      this.toast('Error al eliminar. Verifica tu conexión.', 'error');
    }
  },

  _sponsorBannerNewForm() {
    return '<div class="sr-q-admin-card" style="margin-bottom:12px;border-color:#D4A017">' +
      '<div style="font-size:12px;color:#D4A017;letter-spacing:1px;margin-bottom:10px">+ AGREGAR SPONSOR AL BANNER</div>' +
      '<form onsubmit="SRAdmin.addBannerSponsor(event)">' +
        '<div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:10px">' +
          '<input name="name" placeholder="Nombre del patrocinador" required ' +
            'style="padding:9px 10px;background:#111;border:1px solid #333;border-radius:6px;color:#fff;font-size:13px;box-sizing:border-box">' +
          '<input name="url" placeholder="https://sitio-del-sponsor.com" type="url" ' +
            'style="padding:9px 10px;background:#111;border:1px solid #333;border-radius:6px;color:#fff;font-size:13px;box-sizing:border-box">' +
        '</div>' +
        '<div style="display:flex;align-items:center;gap:12px;margin-bottom:12px">' +
          '<img id="sponsor-banner-new-preview" src="" style="height:32px;max-width:100px;object-fit:contain;background:#0d0d0d;border-radius:4px;display:none">' +
          '<input type="file" accept="image/png,image/jpeg,image/webp" ' +
            'onchange="SRAdmin.handleSponsorLogoInput(event,\'banner-new\',\'sponsor-banner-new-preview\')" ' +
            'style="font-size:12px;color:#888;flex:1">' +
        '</div>' +
        '<button type="submit" class="sr-q-btn sr-q-btn-dark" style="padding:8px 16px;font-size:13px;border-color:#D4A017;color:#D4A017">+ Agregar</button>' +
      '</form>' +
    '</div>';
  },

  async addBannerSponsor(e) {
    e.preventDefault();
    const form = e.target;
    const name = form.querySelector('[name="name"]').value.trim();
    const url = form.querySelector('[name="url"]').value.trim();
    if (!name) return this.toast('Ingresa el nombre del patrocinador', 'error');
    const logo = this._sponsorLogoCache['banner-new'] || '';
    try {
      const existingSnap = await this.db.collection('quiniela_sponsors').where('tier', '==', 'banner').get();
      await this.db.collection('quiniela_sponsors').add({
        name, url, logo, tier: 'banner', active: true,
        order: existingSnap.size, clicks: 0, createdAt: new Date().toISOString()
      });
      delete this._sponsorLogoCache['banner-new'];
      this.toast('✅ Sponsor agregado al banner rotativo', 'success');
      this.renderPatrocinadores();
    } catch (err) {
      if (this.isSessionError(err)) return this.handleSessionExpired();
      this.toast('Error al guardar. Verifica tu conexión.', 'error');
    }
  },

  _sponsorBannerRow(s, idx, total) {
    const logoKey = 'banner-' + s.id;
    const previewId = 'sponsor-banner-preview-' + s.id;
    return '<div class="sr-q-admin-card" style="margin-bottom:10px;' + (s.active === false ? 'opacity:0.5' : '') + '">' +
      '<form onsubmit="SRAdmin.saveBannerSponsor(event,\'' + s.id + '\')">' +
        '<div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:10px;gap:10px;flex-wrap:wrap">' +
          '<label style="display:flex;align-items:center;gap:8px;cursor:pointer;font-size:12px;color:#888">' +
            '<input type="checkbox" ' + (s.active !== false ? 'checked' : '') + ' ' +
              'onchange="SRAdmin.toggleBannerActive(\'' + s.id + '\',this.checked)" style="accent-color:#D4A017">' +
            'Activo' +
          '</label>' +
          '<div style="display:flex;gap:6px;align-items:center">' +
            '<span style="font-size:11px;color:#555">👆 ' + (s.clicks || 0) + ' clics</span>' +
            '<button type="button" onclick="SRAdmin.moveBannerSponsor(\'' + s.id + '\',-1)" ' + (idx === 0 ? 'disabled' : '') +
              ' style="background:#181818;border:1px solid #333;color:#888;padding:4px 8px;border-radius:4px;cursor:pointer">↑</button>' +
            '<button type="button" onclick="SRAdmin.moveBannerSponsor(\'' + s.id + '\',1)" ' + (idx === total - 1 ? 'disabled' : '') +
              ' style="background:#181818;border:1px solid #333;color:#888;padding:4px 8px;border-radius:4px;cursor:pointer">↓</button>' +
            '<button type="button" onclick="SRAdmin.removeBannerSponsor(\'' + s.id + '\')" ' +
              'style="background:#1a0808;border:1px solid #991b1b;color:#f87171;padding:4px 8px;border-radius:4px;cursor:pointer">🗑️</button>' +
          '</div>' +
        '</div>' +
        '<div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:10px">' +
          '<input name="name" value="' + escapeHTML(s.name || '') + '" placeholder="Nombre" required ' +
            'style="padding:9px 10px;background:#111;border:1px solid #333;border-radius:6px;color:#fff;font-size:13px;box-sizing:border-box">' +
          '<input name="url" value="' + escapeHTML(s.url || '') + '" placeholder="https://…" type="url" ' +
            'style="padding:9px 10px;background:#111;border:1px solid #333;border-radius:6px;color:#fff;font-size:13px;box-sizing:border-box">' +
        '</div>' +
        '<div style="display:flex;align-items:center;gap:12px;margin-bottom:10px">' +
          '<img id="' + previewId + '" src="' + escapeHTML(s.logo || '') + '" ' +
            'style="height:28px;max-width:90px;object-fit:contain;background:#0d0d0d;border-radius:4px;' +
            (s.logo ? '' : 'display:none;') + '">' +
          '<input type="file" accept="image/png,image/jpeg,image/webp" ' +
            'onchange="SRAdmin.handleSponsorLogoInput(event,\'' + logoKey + '\',\'' + previewId + '\')" ' +
            'style="font-size:12px;color:#888;flex:1">' +
        '</div>' +
        '<button type="submit" class="sr-q-btn sr-q-btn-dark" style="padding:8px 16px;font-size:12px;border-color:#555">💾 Guardar</button>' +
      '</form>' +
    '</div>';
  },

  async saveBannerSponsor(e, id) {
    e.preventDefault();
    const form = e.target;
    const name = form.querySelector('[name="name"]').value.trim();
    const url = form.querySelector('[name="url"]').value.trim();
    if (!name) return this.toast('Ingresa el nombre del patrocinador', 'error');
    const logoKey = 'banner-' + id;
    const preview = document.getElementById('sponsor-banner-preview-' + id);
    const logo = this._sponsorLogoCache[logoKey] || (preview && preview.src.startsWith('data:') ? preview.src : '');
    try {
      await this.db.collection('quiniela_sponsors').doc(id).update({ name, url, logo });
      delete this._sponsorLogoCache[logoKey];
      this.toast('✅ Sponsor actualizado', 'success');
      this.renderPatrocinadores();
    } catch (err) {
      if (this.isSessionError(err)) return this.handleSessionExpired();
      this.toast('Error al guardar. Verifica tu conexión.', 'error');
    }
  },

  async removeBannerSponsor(id) {
    if (!confirm('¿Eliminar este sponsor del banner rotativo?')) return;
    try {
      await this.db.collection('quiniela_sponsors').doc(id).delete();
      this.toast('Sponsor eliminado', 'info');
      this.renderPatrocinadores();
    } catch (err) {
      if (this.isSessionError(err)) return this.handleSessionExpired();
      this.toast('Error al eliminar. Verifica tu conexión.', 'error');
    }
  },

  async toggleBannerActive(id, active) {
    try {
      await this.db.collection('quiniela_sponsors').doc(id).update({ active });
    } catch (err) {
      if (this.isSessionError(err)) return this.handleSessionExpired();
      this.toast('Error al guardar. Verifica tu conexión.', 'error');
    }
  },

  async moveBannerSponsor(id, direction) {
    try {
      const snap = await this.db.collection('quiniela_sponsors').where('tier', '==', 'banner').get();
      const banners = [];
      snap.forEach(doc => banners.push({ id: doc.id, ...doc.data() }));
      banners.sort((a, b) => (a.order || 0) - (b.order || 0));
      const idx = banners.findIndex(b => b.id === id);
      const swapIdx = idx + direction;
      if (idx === -1 || swapIdx < 0 || swapIdx >= banners.length) return;
      const a = banners[idx], b = banners[swapIdx];
      const batch = this.db.batch();
      batch.update(this.db.collection('quiniela_sponsors').doc(a.id), { order: b.order || 0 });
      batch.update(this.db.collection('quiniela_sponsors').doc(b.id), { order: a.order || 0 });
      await batch.commit();
      this.renderPatrocinadores();
    } catch (err) {
      if (this.isSessionError(err)) return this.handleSessionExpired();
      this.toast('Error al reordenar. Verifica tu conexión.', 'error');
    }
  },

  // ── EXPORT / IMPORT / RESET ──────────────────────────────

  async exportData() {
    try {
      const [{ matches, results, admin }, { users, predictions }, usernamesSnap] = await Promise.all([
        this.getConfig(), this.getUsersWithPredictions(), this.db.collection('usernames').get()
      ]);
      const usernames = {};
      usernamesSnap.forEach(doc => { usernames[doc.id] = doc.data(); });
      const blob = new Blob([JSON.stringify({ matches, results, admin, users, predictions, usernames }, null, 2)],
        { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'quiniela-mundial-2026-' + new Date().toISOString().split('T')[0] + '.json';
      a.click();
      URL.revokeObjectURL(url);
      this.toast('📥 Datos exportados', 'success');
    } catch (err) {
      if (this.isSessionError(err)) return this.handleSessionExpired();
      this.toast('Error al exportar. Verifica tu conexión.', 'error');
    }
  },

  importData(e) {
    const file = e.target.files && e.target.files[0];
    e.target.value = ''; // permitir volver a seleccionar el mismo archivo después
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async () => {
      let parsed;
      try {
        parsed = JSON.parse(reader.result);
      } catch (err) {
        return this.toast('El archivo no es un JSON válido', 'error');
      }
      if (!parsed.matches || !parsed.users) {
        return this.toast('Este archivo no tiene el formato de respaldo esperado', 'error');
      }
      if (!confirm('⚠️ Esto REEMPLAZARÁ partidos, resultados, fases, premios, perfiles y predicciones actuales con los del respaldo. Las cuentas de Authentication (login) NO se modifican. ¿Continuar?')) {
        return;
      }

      try {
        const writes = [
          this.db.collection('quiniela_config').doc('matches').set(parsed.matches),
          this.db.collection('quiniela_config').doc('results').set(parsed.results || {}),
          this.db.collection('quiniela_config').doc('admin').set(parsed.admin || { fasesActivas: ['dieciseisavos'], premios: null })
        ];
        (parsed.users || []).forEach(u => {
          const { id, ...profile } = u;
          if (id) writes.push(this.db.collection('users').doc(id).set(profile));
        });
        Object.keys(parsed.predictions || {}).forEach(uid => {
          writes.push(this.db.collection('predictions').doc(uid).set(parsed.predictions[uid]));
        });
        Object.keys(parsed.usernames || {}).forEach(lower => {
          writes.push(this.db.collection('usernames').doc(lower).set(parsed.usernames[lower]));
        });
        await Promise.all(writes);
        this.toast('✅ Respaldo restaurado correctamente', 'success');
        this.renderStats();
        this.switchTab('fases');
      } catch (err) {
        if (this.isSessionError(err)) return this.handleSessionExpired();
        this.toast('Error al restaurar. Verifica tu conexión.', 'error');
      }
    };
    reader.onerror = () => this.toast('No se pudo leer el archivo', 'error');
    reader.readAsText(file);
  },

  async resetAll() {
    if (!confirm('⚠️ ¿Eliminar TODOS los datos de la quiniela? Esta acción no se puede deshacer.')) return;
    if (!confirm('¿Estás seguro? Se borrarán TODOS los usuarios, predicciones, resultados y configuración. Las cuentas de inicio de sesión en Firebase Authentication NO se eliminan (eso se hace manualmente desde la consola de Firebase).')) return;

    try {
      const collections = ['users', 'predictions', 'usernames'];
      for (const name of collections) {
        const snap = await this.db.collection(name).get();
        const docs = snap.docs || [];
        for (let i = 0; i < docs.length; i += 450) {
          const batch = this.db.batch();
          docs.slice(i, i + 450).forEach(d => batch.delete(this.db.collection(name).doc(d.id)));
          await batch.commit();
        }
      }
      await Promise.all([
        this.db.collection('quiniela_config').doc('matches').delete(),
        this.db.collection('quiniela_config').doc('results').delete(),
        this.db.collection('quiniela_config').doc('admin').delete(),
        this.db.collection('quiniela_config').doc('stats').delete()
      ]);
      this.toast('Todos los datos eliminados. Recarga la página.', 'warning');
      setTimeout(() => location.reload(), 2000);
    } catch (err) {
      if (this.isSessionError(err)) return this.handleSessionExpired();
      this.toast('Error al eliminar. Verifica tu conexión.', 'error');
    }
  },

  // ── TOAST ───────────────────────────────────────────────

  toast(msg, type) {
    type = type || 'info';
    let el = document.getElementById('sr-admin-toast');
    if (!el) {
      el = document.createElement('div');
      el.id = 'sr-admin-toast';
      el.className = 'sr-q-toast';
      document.body.appendChild(el);
    }
    el.textContent = msg;
    el.className = 'sr-q-toast sr-q-toast-' + type + ' show';
    clearTimeout(el._t);
    el._t = setTimeout(() => el.classList.remove('show'), 4000);
  },

  // ── TAB: VISTA GENERAL ──────────────────────────────────

  async renderVista() {
    const container = document.getElementById('sr-admin-vista-content');
    if (!container) return;
    container.innerHTML = '<p style="color:#666">Cargando…</p>';

    try {
      const [{ matches, results, admin }, { users, predictions }] = await Promise.all([
        this.getConfig(),
        this.getUsersWithPredictions()
      ]);

      const fasesActivas = admin.fasesActivas || [];
      const resultKeys = Object.keys(results);

      // Mapa matchId → faseId para contar predicciones por fase
      const matchFaseMap = {};
      this.fases.forEach(f => {
        (matches[f.id] || []).forEach(m => { matchFaseMap[m.id] = f.id; });
      });

      const predsByFase = {};
      this.fases.forEach(f => { predsByFase[f.id] = 0; });
      Object.values(predictions).forEach(preds => {
        Object.keys(preds).forEach(matchId => {
          const faseId = matchFaseMap[matchId];
          if (faseId !== undefined) predsByFase[faseId]++;
        });
      });

      const totalPreds = Object.values(predsByFase).reduce((a, b) => a + b, 0);

      // Tarjetas de resumen
      let html = '<div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(130px,1fr));gap:10px;margin-bottom:24px">';
      html += this._vistaStatCard('👥', users.length, 'Participantes');
      html += this._vistaStatCard('📝', totalPreds, 'Predicciones');
      html += this._vistaStatCard('✅', resultKeys.length, 'Finalizados');
      html += this._vistaStatCard('🔥', fasesActivas.length, 'Fases activas');
      html += '</div>';

      // Tarjeta por fase
      html += '<div style="display:flex;flex-direction:column;gap:12px">';

      this.fases.forEach(fase => {
        const partidosFase = matches[fase.id] || [];
        const total      = partidosFase.length;
        const done       = partidosFase.filter(m => m.estado === 'finalizado').length;
        const bloqueados = partidosFase.filter(m => m.estado === 'bloqueado').length;
        const pendientes = partidosFase.filter(m => m.estado === 'pendiente').length;
        const predsCount = predsByFase[fase.id] || 0;
        const isActive   = fasesActivas.includes(fase.id);
        const isComplete = done === total && total > 0;
        const pct        = total > 0 ? Math.round((done / total) * 100) : 0;

        const borderColor = isComplete ? '#1a4a1a' : isActive ? '#3a2a00' : '#2a2a2a';
        const barColor    = isComplete ? '#22c55e' : '#D4A017';
        const statusColor = isComplete ? '#22c55e' : isActive ? '#D4A017' : '#555';
        const statusLabel = isComplete ? '✅ Completada' : isActive ? '🔥 En curso' : '🔒 Pendiente';

        html += '<div style="background:#1a1a1a;border:1px solid ' + borderColor + ';border-radius:12px;padding:16px 18px">';

        // Encabezado
        html += '<div style="display:flex;align-items:center;justify-content:space-between;gap:12px;flex-wrap:wrap;margin-bottom:12px">';
        html += '<div style="display:flex;align-items:center;gap:12px">';
        html += '<span style="font-size:26px">' + fase.icono + '</span>';
        html += '<div>';
        html += '<div style="font-family:\'Bebas Neue\',sans-serif;font-size:17px;color:#D4A017;letter-spacing:2px">' + escapeHTML(fase.nombre) + '</div>';
        html += '<div style="font-size:11px;color:' + statusColor + ';margin-top:2px">' + statusLabel + '</div>';
        html += '</div></div>';

        // Chips de estado
        html += '<div style="display:flex;gap:6px;flex-wrap:wrap">';
        html += '<span style="background:#252525;border-radius:6px;padding:3px 9px;font-size:11px;color:#aaa">' + done + '/' + total + ' jugados</span>';
        if (pendientes > 0) html += '<span style="background:#0a2a0a;border-radius:6px;padding:3px 9px;font-size:11px;color:#86efac">▶ ' + pendientes + ' abiertos</span>';
        if (bloqueados > 0) html += '<span style="background:#2a2008;border-radius:6px;padding:3px 9px;font-size:11px;color:#fbbf24">🔒 ' + bloqueados + '</span>';
        html += '<span style="background:#0a1828;border-radius:6px;padding:3px 9px;font-size:11px;color:#60a5fa">📝 ' + predsCount + '</span>';
        html += '</div></div>';

        // Barra de progreso
        html += '<div style="background:#2a2a2a;border-radius:999px;height:5px;overflow:hidden;margin-bottom:10px">';
        html += '<div style="height:100%;width:' + pct + '%;background:' + barColor + ';border-radius:999px;transition:width 0.5s ease"></div>';
        html += '</div>';

        // Puntos de cada partido
        if (total > 0) {
          html += '<div style="display:flex;flex-wrap:wrap;gap:5px">';
          partidosFase.forEach(m => {
            const mDone   = m.estado === 'finalizado';
            const mOpen   = m.estado === 'pendiente';
            const dotBg   = mDone ? '#0a2a0a' : mOpen ? '#2a1c00' : '#1a1a1a';
            const dotBord = mDone ? '#22c55e' : mOpen ? '#D4A017' : '#444';
            const dotClr  = mDone ? '#22c55e' : mOpen ? '#D4A017' : '#555';
            const tipLocal = m.local && m.local.nombre !== 'Por definir' ? escapeHTML(m.local.nombre) : '?';
            const tipVisit = m.visitante && m.visitante.nombre !== 'Por definir' ? escapeHTML(m.visitante.nombre) : '?';
            html += '<div title="P' + m.num + ': ' + tipLocal + ' vs ' + tipVisit + '" ' +
              'style="width:26px;height:26px;border-radius:5px;background:' + dotBg + ';border:1px solid ' + dotBord + ';' +
              'display:flex;align-items:center;justify-content:center;font-size:9px;color:' + dotClr + ';' +
              'font-family:\'Bebas Neue\',sans-serif;cursor:default">' + m.num + '</div>';
          });
          html += '</div>';
        }

        html += '</div>';
      });

      html += '</div>';
      container.innerHTML = html;

    } catch (err) {
      if (this.isSessionError(err)) return this.handleSessionExpired();
      container.innerHTML = '<p style="color:#ef4444;font-size:14px">Error al cargar. Verifica tu conexión.</p>';
    }
  },

  _vistaStatCard(icon, value, label) {
    return '<div style="background:#1a1a1a;border:1px solid #2a2a2a;border-radius:10px;padding:14px;text-align:center">' +
      '<div style="font-size:20px;margin-bottom:5px">' + icon + '</div>' +
      '<div style="font-family:\'Bebas Neue\',sans-serif;font-size:26px;color:#D4A017;letter-spacing:1px;font-variant-numeric:tabular-nums">' + value + '</div>' +
      '<div style="font-size:10px;color:#555;letter-spacing:1.5px;text-transform:uppercase;margin-top:2px">' + label + '</div>' +
    '</div>';
  }

};

document.addEventListener('DOMContentLoaded', function() { SRAdmin.init(); });
